import React, { useState, useRef, useCallback} from "react";
import {
  Stepper,
  Step,
  StepLabel,
  Button,
  CircularProgress,
} from "@mui/material";
import {
  TextField,
  MenuItem,
  Box,
  Typography,
  FormControlLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Autocomplete,
} from "@mui/material";
import { Radio, RadioGroup, FormControl } from "@mui/material";
import Navbar from "../../../globals/components/Navbar";
import GuestNavbar from "../GuestNavbar";
import { useEffect } from "react";
import { apiClient } from "../../../globals/utils/api";
import { renderTrusteePrincipalAttorneySearch } from "../../../globals/components/StandardPrincipalAttorneySearchSection";
import { handleStandardPrincipalAttorneyHomeAddressChange } from "../../../globals/utils/bondGuardianAttorneyWiring";
import CreatableSelect from "react-select/creatable";
import { FixedSizeList as List } from "react-window";
import { useSelector } from "react-redux";
import debounce from "lodash.debounce";
import { CheckCircle, WarningAmber } from "@mui/icons-material";
import { toast } from "react-toastify";
import InputMask from "react-input-mask";
import { NumericFormat } from "react-number-format";
import { useNavigate, useLocation } from "react-router-dom";
import { useParams } from "react-router-dom";
import { calculatePremium } from "../../../globals/utils/commons";
import Alert from "@mui/material/Alert";
import AlertTitle from "@mui/material/AlertTitle";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { loadGoogleMaps } from "../../../globals/utils/googleMapsLoader";
import Stack from "@mui/material/Stack";
import { BillingInformationForm } from "./Helpers/BillingInformation";
import { ObligeeInformationForm } from "./Helpers/ObligeeInformation";
import { buildPrincipalErrorMessages } from "./Helpers/principalErrorMessages";
import { is_prod } from "../../../globals/utils/constants";
import { extractErrorMessage } from "../../../globals/utils/error-handler";
import { TRUSTEE_FIELD_LIMITS, validateFieldLengths } from "../../../globals/utils/bondFieldLimits";
import SupportRequestModal from "../../../components/SupportRequestModal";
import { createSupportRequest } from "../../../services/supportRequestService";
import CalculatorIcon from "@mui/icons-material/Calculate";
import EmailIcon from "@mui/icons-material/Email";
import PremiumCalculator from "../PremiumCalculatorPublic";
import SupportContactDialog from "../../../components/SupportContactDialog";

// Custom dropdown styling (matches MUI styles)
const customStyles = {
  control: (provided) => ({
    ...provided,
    backgroundColor: "transparent", // Make the background transparent
    minHeight: "56px",
    border: "1px solid rgba(0, 0, 0, 0.23)",
    borderRadius: "4px",
    boxShadow: "none",
    fontSize: "16px", // Decrease font size
    "&:hover": {
      borderColor: "rgba(0, 0, 0, 0.87)",
    },
  }),
  menu: (base) => ({
    ...base,
    zIndex: 9999, // Increase z-index for dropdown menu
    fontSize: "16px", // Decrease font size
  }),
  menuPortal: (base) => ({
    ...base,
    zIndex: 9999, // Increase z-index for menu portal
  }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isSelected
      ? "#6200ea"
      : state.isFocused
        ? "#f0f0f0"
        : "#fff",
    color: state.isSelected ? "#fff" : "#000",
    fontSize: "16px", // Decrease font size
    "&:hover": {
      backgroundColor: "#f0f0f0",
    },
  }),
};

const NumberFormatCustom = React.forwardRef(function NumberFormatCustom(
  props,
  ref
) {
  const { onChange, ...other } = props;

  return (
    <NumericFormat
      {...other}
      getInputRef={ref}
      onValueChange={(values) => {
        onChange({
          target: {
            name: props.name,
            value: values.value,
          },
        });
      }}
      thousandSeparator
      isNumericString
      prefix="$"
      fixedDecimalScale
    />
  );
});

// Parse currency/amount input safely (keeps decimals).
// - `react-number-format` typically gives "38000.55"
// - also tolerates "$38,000.55"
const parseCurrencyToNumber = (value) => {
  if (value === null || value === undefined) return NaN;
  if (typeof value === "number") return Number.isFinite(value) ? value : NaN;

  const raw = String(value).trim();
  if (!raw) return NaN;

  const noThousands = raw.replace(/,/g, "");
  const cleaned = noThousands.replace(/[^0-9.]/g, "");
  if (!cleaned) return NaN;

  // If multiple dots exist, treat first as decimal separator and strip the rest.
  const [intPart, ...fracParts] = cleaned.split(".");
  const normalized = fracParts.length ? `${intPart}.${fracParts.join("")}` : intPart;

  const num = Number(normalized);
  return Number.isFinite(num) ? num : NaN;
};

const safeNumber = (num, fallback = 0) => (Number.isFinite(num) ? num : fallback);

// Employment Status <-> stored occupation helpers.
// We store both values inside the existing `occupation` field using a hyphen separator:
// - "Unemployed"
// - "Employed - Accountant"
// - "Retired - Teacher"
const splitEmploymentOccupation = (raw) => {
  const value = (raw || "").trim();
  if (!value) return { employmentStatus: "", occupationText: "" };
  const parts = value.split("-").map((p) => p.trim()).filter(Boolean);
  if (parts.length === 0) return { employmentStatus: "", occupationText: "" };
  const recognized = new Set(["Employed", "Unemployed", "Retired"]);
  const first = parts[0];
  // Backwards compatibility: older records used to store only the occupation text.
  if (!recognized.has(first)) {
    return { employmentStatus: "Employed", occupationText: value };
  }
  return { employmentStatus: first, occupationText: parts.slice(1).join(" - ") };
};

const joinEmploymentOccupation = (employmentStatus, occupationText) => {
  const status = (employmentStatus || "").trim();
  const occ = (occupationText || "").trim();
  if (!status) return "";
  if (status === "Unemployed") return "Unemployed";
  if (!occ) return status;
  return `${status} - ${occ}`;
};

// Labeled CreatableSelect Component
const LabeledCreatableSelect = ({
  label,
  options,
  value,
  onChange,
  placeholder,
}) => {
  return (
    <Box sx={{ marginBottom: "2px" }}>
      {/* Label styled to mimic MUI's TextField label */}
      <Typography
        sx={{
          fontSize: "12px",
          color: "rgba(0, 0, 0, 0.6)",
          marginBottom: "4px",
          fontWeight: 500,
        }}
      >
        {label}
      </Typography>
      <CreatableSelect
        isClearable
        options={options}
        components={{ MenuList }}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        styles={customStyles}
      />
    </Box>
  );
};

// Virtualized MenuList for better performance
const MenuList = (props) => {
  const height = 35;
  const { options, children, maxHeight } = props;

  return (
    <List
      height={Math.min(maxHeight, options.length * height)}
      itemCount={children.length}
      itemSize={height}
    >
      {({ index, style }) => <div style={style}>{children[index]}</div>}
    </List>
  );
};

// Google Autocomplete Component
const GoogleAutoComplete = ({ placeholder, callback }) => {
  const autoCompleteRef = useRef();
  const inputRef = useRef();
  const [loaded, setLoaded] = useState(false);

  const loadGoogleMapsScript = useCallback(() => {
    loadGoogleMaps()
      .then(() => setLoaded(true))
      .catch((e) => console.error(e));
  }, []);

  useEffect(() => {
    loadGoogleMapsScript();
  }, [loadGoogleMapsScript]);

  useEffect(() => {
    if (loaded && window.google) {
      autoCompleteRef.current = new window.google.maps.places.Autocomplete(
        inputRef.current,
        {
          fields: ["address_components", "geometry"],
          types: ["address"], // better for full addresses
        }
      );

      autoCompleteRef.current.addListener("place_changed", () => {
        const place = autoCompleteRef.current.getPlace();

        if (!place || !place.address_components) {
          console.error("No valid place returned or missing address components.");
          return;
        }

        let address = "",
          city = "",
          state = "",
          zipCode = "";

        place.address_components.forEach((component) => {
          const types = component.types;

          if (types.includes("street_number")) {
            address = `${component.long_name} ${address}`;
          }
          if (types.includes("route")) {
            address += component.long_name;
          }

          // City fallbacks
          if (types.includes("locality")) {
            city = component.long_name;
          }
          if (!city && (types.includes("sublocality") || types.includes("sublocality_level_1"))) {
            city = component.long_name;
          }
          if (!city && types.includes("administrative_area_level_2")) {
            city = component.long_name;
          }

          if (types.includes("administrative_area_level_1")) {
            state = component.long_name; // ✅ full state name
          }

          if (types.includes("postal_code")) {
            zipCode = component.long_name;
          }
        });

        // Fallback: geocoder if city/state missing
        if ((!city || !state) && place.geometry?.location) {
          const geocoder = new window.google.maps.Geocoder();
          geocoder.geocode({ location: place.geometry.location }, (results, status) => {
            if (status === "OK" && results[0]) {
              results[0].address_components.forEach((component) => {
                const types = component.types;
                if (!city && types.includes("locality")) {
                  city = component.long_name;
                }
                if (!state && types.includes("administrative_area_level_1")) {
                  state = component.long_name; // ✅ full name
                }
              });
              if (callback) callback(address, city, state, zipCode);
            }
          });
        } else {
          if (callback) callback(address, city, state, zipCode);
        }
      });
    }
  }, [loaded, callback]);

  return (
    <TextField
      variant="outlined"
      fullWidth
      placeholder={placeholder}
      inputRef={inputRef}
      sx={{ marginBottom: "8px" }}
    />
  );
};

const scrollToTop = () => {
  window.scrollTo({
    top: 0,
    behavior: "smooth", // Smooth scrolling
  });
};

const BasicInformationForm = ({ globalData = {}, data = {}, updateData, errors = {} }) => {
  const [state, setState] = useState(null); // Initialize state as null
  const [type, setType] = useState(null); // Initialize bondType as null
  const [counties, setCounties] = useState([]);
  const [loadingCounties, setLoadingCounties] = useState(false); // Loading state for counties dropdown

  // Handle input change and directly update the global state
  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === "bondAmount") {
      const updatedData = { ...data, [name]: value };
      updateData(updatedData);

      if (updatedData.bondAmount) {
        const bondAmount = parseCurrencyToNumber(updatedData.bondAmount);
        if (Number.isFinite(bondAmount)) {
          const premium = calculatePremium(bondAmount);
          updateData({
            ...updatedData,
            premiumAmount: premium,
            PrincipalNumber: "1",
            is_esignatures: "No",
          });
        }
      }
    } else {
      updateData({ ...data, [name]: value });
    }
  };

  const handleAutocompleteChange = (event, value, fieldName) => {
    updateData({ ...data, [fieldName]: value });
  };

  // Fetch state and bondType from localStorage or props on mount
  useEffect(() => {
    const savedState = localStorage.getItem("selectedState");
    const savedType = localStorage.getItem("selectedBondType");

    if (savedState && !state) {
      try {
        setState(JSON.parse(savedState));
      } catch (e) { }
    }

    if (savedType && !type) {
      try { setType(JSON.parse(savedType)); } catch (e) { }
    }

    scrollToTop();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Hydrate local 'state' and 'type' if data (from props) changes (e.g., during Resume)
  useEffect(() => {
    const sName = data?.state;
    const tName = data?.bondType;
    if (sName && (!state || state.label !== sName)) {
      const savedState = localStorage.getItem("selectedState");
      if (savedState) {
        try {
          const parsed = JSON.parse(savedState);
          if (parsed.label === sName) {
            setState(parsed);
          }
        } catch (e) { }
      }
    }
    if (tName && (!type || type.label !== tName)) {
      const savedType = localStorage.getItem("selectedBondType");
      if (savedType) {
        try {
          const parsed = JSON.parse(savedType);
          if (parsed.label === tName) {
            setType(parsed);
          }
        } catch (e) { }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.state, data?.bondType]);

  // Fetch counties when state changes
  useEffect(() => {
    const fetchCounties = async () => {
      if (state && state.id) {
        setLoadingCounties(true); // Start loader
        try {
          const countyResponse = await apiClient(
            `/counties-by-state/${state.id}/`,
            "GET"
          );
          const formattedCounties = countyResponse.map(
            (county) => county.bond_county
          );
          setCounties(formattedCounties);
        } catch (error) {
          console.error("Error fetching counties:", error);
        } finally {
          setLoadingCounties(false); // Stop loader
        }
      }
    };

    fetchCounties();
  }, [state]);

  // Format numbers as US currency
  const formatAsCurrency = (value) => {
    if (!value) return "";
    const numericValue = parseFloat(value);
    return numericValue.toLocaleString("en-US", {
      style: "currency",
      currency: "USD",
    });
  };

  return (
    <Box
      className="bg-gray-100 p-6 rounded-lg shadow-md max-w-3xl mx-auto mt-8"
      sx={{
        border: "1px solid #e0e0e0",
      }}
    >
      <Typography variant="h5" className="font-bold mb-4">
        Basic Information
      </Typography>

      <Box className="grid grid-cols-1 gap-4">
        {" "}
        {/* Consistent spacing */}
        <Autocomplete
          options={counties}
          value={data.county || ""}
          onChange={(event, value) =>
            handleAutocompleteChange(event, value, "county")
          }
          loading={loadingCounties}
          renderInput={(params) => (
            <TextField
              {...params}
              label="County"
              fullWidth
              variant="outlined"
              required
              error={!!errors.county}
              helperText={errors.county}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {loadingCounties ? <CircularProgress size={20} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
        />
        <TextField
          label="Full Legal Name of Ward/Beneficiary  "
          name="legalName"
          value={data.legalName || ""}
          onChange={handleChange}
          fullWidth
          required
          error={!!errors.legalName}
          helperText={errors.legalName}
        />
        <TextField
          label="Formal Name of the Trust"
          name="trustName"
          value={data.trustName || ""}
          onChange={handleChange}
          fullWidth
          required
          error={!!errors.trustName}
          helperText={errors.trustName}
          inputProps={{ maxLength: 200 }}
        />
        <TextField
          label="Date of Birth of Ward/Beneficiary"
          name="dateOfBirth"
          value={data.dateOfBirth || ""}
          onChange={handleChange}
          type="date"
          fullWidth
          required
          error={!!errors.dateOfBirth}
          helperText={errors.dateOfBirth}
          InputLabelProps={{ shrink: true }}
          inputProps={{
            max: new Date().toISOString().split('T')[0]
          }}
        />
        <TextField
          label="Bond Amount"
          name="bondAmount"
          value={data.bondAmount || ""}
          onChange={handleChange}
          fullWidth
          required
          error={!!errors.bondAmount}
          helperText={errors.bondAmount}
          InputProps={{
            inputComponent: NumberFormatCustom,
          }}
        />
        <TextField
          label="Premium Amount"
          name="premiumAmount"
          value={formatAsCurrency(data.premiumAmount)}
          fullWidth
          required
          disabled
        />
        <TextField
          select
          label="Do you have an official court case number?"
          name="hasCourtCaseNumber"
          value={data.hasCourtCaseNumber || ""}
          onChange={(e) => {
            const val = e.target.value;
            if (val === "No") {
              updateData({ ...data, hasCourtCaseNumber: "No", courtCaseNumber: "" });
            } else {
              updateData({ ...data, hasCourtCaseNumber: val });
            }
          }}
          fullWidth
        >
          <MenuItem value="Yes">Yes</MenuItem>
          <MenuItem value="No">No</MenuItem>
        </TextField>
        {data.hasCourtCaseNumber === "Yes" && (
          <TextField
            label="Court File #"
            name="courtCaseNumber"
            value={data.courtCaseNumber || ""}
            onChange={handleChange}
            fullWidth
            required
            error={!!errors.courtCaseNumber}
            helperText={errors.courtCaseNumber}
            inputProps={{ maxLength: 30 }}
          />
        )}
        <TextField
          label="Number Of Principals"
          name="PrincipalNumber"
          value={data.PrincipalNumber || 1}
          onChange={handleChange}
          fullWidth
          required
          error={!!errors.PrincipalNumber}
          helperText={errors.PrincipalNumber}
          type="number"
          InputProps={{
            inputProps: {
              min: 1,
              max: 4,
            },
          }}
        />
        {/* Add a new dropdown for e-signature preference */}
        <TextField
          select
          label="Will you prefer to e-sign the Indemnity Agreement?"
          name="is_esignatures"
          value={data.is_esignatures || "No"}
          onChange={handleChange}
          fullWidth
          required
          error={!!errors.is_esignatures}
          helperText={errors.is_esignatures}
        >
          <MenuItem value="Yes">Yes</MenuItem>
          <MenuItem value="No">No</MenuItem>
        </TextField>
      </Box>
    </Box>
  );
};

const GeneralInformationForm = ({ data = {}, updateData, globalData, errors = {} }) => {
  const navigate = useNavigate();
  const handleChange = (e) => {
    const { name, value } = e.target;
    updateData({ ...data, [name]: value });
  };

  const handleExplanationChange = (e) => {
    const { name, value } = e.target;
    updateData({
      ...data,
      explanations: {
        ...(data?.explanations || {}),
        [name]: value || "", // Ensure empty explanation is handled
      },
    });
  };

  useEffect(() => {
    scrollToTop();
  }, []);

  useEffect(() => {
    if (data.allUSUKCanada === "No") {
      const timer = setTimeout(() => {
        navigate(-1);
      }, 5000); // 5000ms = 5 seconds

      return () => clearTimeout(timer); // Cleanup function to clear timeout if component unmounts or dependency changes
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.allUSUKCanada]);

  // Ensure `explanations` exists
  const explanations = data?.explanations || {
    ongoingBusiness: "",
    // didLastWillAndTestament: "",
    indebtedGuardians: "",
    // replacingFiduciary: "",
    replacingPriorBond: "",
    // demandedByInterestedParty: "",
    // jointControlExercised: "",
    supplementalNeedsTrust: "",
    // outstandingLawsuitExplanation: "",
    // bankruptOrInsolventExplanation: "",
    isDeclined: "",
  };

  return (
    <Box
      className="bg-gray-100 p-6 rounded-lg shadow-md max-w-3xl mx-auto mt-8"
      sx={{
        border: "1px solid #e0e0e0",
      }}
    >
      <Typography variant="h5" className="font-bold mb-4">
        General Information
      </Typography>

      <Box className="grid grid-cols-1 gap-4">
        {/* US Uk Canada? */}
        <TextField
          select
          label="Are all Trustees, U.S, U.K or Canada citizens??"
          name="allUSUKCanada"
          value={data.allUSUKCanada || "Yes"}
          onChange={handleChange}
          fullWidth
          required
          error={!!errors.allUSUKCanada}
          helperText={errors.allUSUKCanada}
        >
          <MenuItem value="Yes">Yes</MenuItem>
          <MenuItem value="No">No</MenuItem>
        </TextField>

        {data.allUSUKCanada === "No" && (
          <Stack sx={{ width: "100%", mb: 2 }} spacing={2}>
            <Alert
              severity="warning"
              icon={<WarningAmberIcon fontSize="inherit" />}
            >
              <AlertTitle>Warning</AlertTitle>
              The court order by <strong>Judge Stearn</strong> is a mandatory
              requirement to create this bond. Please try again once the order
              is issued.
            </Alert>
          </Stack>
        )}

        {/* Ongoing Business */}
        <TextField
          select
          label="Will any of the business of the wards be operated by the trustee?"
          name="ongoingBusiness"
          value={data.ongoingBusiness || "No"}
          onChange={handleChange}
          fullWidth
          required
          error={!!errors.ongoingBusiness}
          helperText={errors.ongoingBusiness}
        >
          <MenuItem value="Yes">Yes</MenuItem>
          <MenuItem value="No">No</MenuItem>
        </TextField>
        {data.ongoingBusiness === "Yes" && (
          <TextField
            label="Provide an explanation"
            name="ongoingBusiness"
            value={explanations.ongoingBusiness || ""}
            onChange={handleExplanationChange}
            fullWidth
          />
        )}

        {/* Last Will and Testament */}
        {/* <TextField
          select
          label="Did beneficiary execute a last will and testament?"
          name="didLastWillAndTestament"
          value={data.didLastWillAndTestament || "No"}
          onChange={handleChange}
          fullWidth
          required
          error={!!errors.didLastWillAndTestament}
          helperText={errors.didLastWillAndTestament}

        >
          <MenuItem value="Yes">Yes</MenuItem>
          <MenuItem value="No">No</MenuItem>
        </TextField> */}

        {/* Indebted Guardians */}
        <TextField
          select
          label="Are any of the trustees indebted to the Estate?"
          name="indebtedGuardians"
          value={data.indebtedGuardians || "No"}
          onChange={handleChange}
          fullWidth
          required
          error={!!errors.indebtedGuardians}
          helperText={errors.indebtedGuardians}
        >
          <MenuItem value="Yes">Yes</MenuItem>
          <MenuItem value="No">No</MenuItem>
        </TextField>
        {data.indebtedGuardians === "Yes" && (
          <TextField
            label="Provide an explanation"
            name="indebtedGuardians"
            value={explanations.indebtedGuardians || ""}
            onChange={handleExplanationChange}
            fullWidth
          />
        )}

        {/* Replacing Fiduciary */}
        {/* <TextField
          select
          label="Does applicant replace any previous fiduciary?"
          name="replacingFiduciary"
          value={data.replacingFiduciary || "No"}
          onChange={handleChange}
          fullWidth
          required
          error={!!errors.replacingFiduciary}
          helperText={errors.replacingFiduciary}

        >
          <MenuItem value="Yes">Yes</MenuItem>
          <MenuItem value="No">No</MenuItem>
        </TextField>
        {data.replacingFiduciary === "Yes" && (
          <TextField
            label="Provide an explanation"
            name="replacingFiduciary"
            value={explanations.replacingFiduciary || ""}
            onChange={handleExplanationChange}
            fullWidth
          />
        )} */}

        {/* Replacing Prior Bond */}
        <TextField
          select
          label="Does this bond replace any prior bond?"
          name="replacingPriorBond"
          value={data.replacingPriorBond || "No"}
          onChange={handleChange}
          fullWidth
          required
          error={!!errors.replacingPriorBond}
          helperText={errors.replacingPriorBond}
        >
          <MenuItem value="Yes">Yes</MenuItem>
          <MenuItem value="No">No</MenuItem>
        </TextField>
        {data.replacingPriorBond === "Yes" && (
          <TextField
            label="Provide an explanation"
            name="replacingPriorBond"
            value={explanations.replacingPriorBond || ""}
            onChange={handleExplanationChange}
            fullWidth
          />
        )}

        {/* Declined By Surety */}
        {/* <TextField
          select
          label="Has the applicant for this bond been declined by another Surety?"
          name="isDeclined"
          value={data.isDeclined || "No"}
          onChange={handleChange}
          fullWidth
          required
          error={!!errors.isDeclined}
          helperText={errors.isDeclined}

        >
          <MenuItem value="Yes">Yes</MenuItem>
          <MenuItem value="No">No</MenuItem>
        </TextField>
        {data.isDeclined === "Yes" && (
          <TextField
            label="Provide an explanation"
            name="isDeclined"
            value={explanations.isDeclined || ""}
            onChange={handleExplanationChange}
            fullWidth
          />
        )} */}

        {/* Demanded by Interested Party */}
        {/* <TextField
          select
          label="Is this bond required on the demand of an interested party other than the Court?"
          name="demandedByInterestedParty"
          value={data.demandedByInterestedParty || "No"}
          onChange={handleChange}
          fullWidth
          required
          error={!!errors.demandedByInterestedParty}
          helperText={errors.demandedByInterestedParty}

        >
          <MenuItem value="Yes">Yes</MenuItem>
          <MenuItem value="No">No</MenuItem>
        </TextField>
        {data.demandedByInterestedParty === "Yes" && (
          <TextField
            label="Provide an explanation"
            name="demandedByInterestedParty"
            value={explanations.demandedByInterestedParty || ""}
            onChange={handleExplanationChange}
            fullWidth
          />
        )} */}

        {/* Outstanding Lawsuit */}
        {/* <TextField
          select
          label="Does the applicant have any outstanding lawsuits, judgment, or liens?"
          name="outstandingLawsuit"
          value={data.outstandingLawsuit || "No"}
          onChange={handleChange}
          fullWidth
          required
          error={!!errors.outstandingLawsuit}
          helperText={errors.outstandingLawsuit}

        >
          <MenuItem value="Yes">Yes</MenuItem>
          <MenuItem value="No">No</MenuItem>
        </TextField>
        {data.outstandingLawsuit === "Yes" && (
          <TextField
            label="Provide an explanation"
            name="outstandingLawsuitExplanation"
            value={explanations.outstandingLawsuitExplanation || ""}
            onChange={handleExplanationChange}
            fullWidth
          />
        )} */}

        {/* Ever Bankrupt */}
        {/* <TextField
          select
          label="Has the applicant ever been bankrupt or insolvent?"
          name="bankruptOrInsolvent"
          value={data.bankruptOrInsolvent || "No"}
          onChange={handleChange}
          fullWidth
          required
          error={!!errors.bankruptOrInsolvent}
          helperText={errors.bankruptOrInsolvent}

        >
          <MenuItem value="Yes">Yes</MenuItem>
          <MenuItem value="No">No</MenuItem>
        </TextField>
        {data.bankruptOrInsolvent === "Yes" && (
          <TextField
            label="Provide an explanation"
            name="bankruptOrInsolventExplanation"
            value={explanations.bankruptOrInsolventExplanation || ""}
            onChange={handleExplanationChange}
            fullWidth
          />
        )} */}

        {/* Will joint control be exercised? */}
        {/* <TextField
          select
          label="Will joint control be exercised?"
          name="jointControlExercised"
          value={data.jointControlExercised || "No"}
          onChange={handleChange}
          fullWidth
          required
          error={!!errors.jointControlExercised}
          helperText={errors.jointControlExercised}

        >
          <MenuItem value="Yes">Yes</MenuItem>
          <MenuItem value="No">No</MenuItem>
        </TextField>
        {data.jointControlExercised === "Yes" && (
          <TextField
            label="Provide an explanation"
            name="jointControlExercised"
            value={explanations.jointControlExercised || ""}
            onChange={handleExplanationChange}
            fullWidth
          />
        )} */}

        {/* Will a supplemental needs trust be created? */}
        <TextField
          select
          label="Will a supplemental needs trust be created?"
          name="supplementalNeedsTrust"
          value={data.supplementalNeedsTrust || "No"}
          onChange={handleChange}
          fullWidth
          required
          error={!!errors.supplementalNeedsTrust}
          helperText={errors.supplementalNeedsTrust}
        >
          <MenuItem value="Yes">Yes</MenuItem>
          <MenuItem value="No">No</MenuItem>
        </TextField>
        {/* {data.supplementalNeedsTrust === "Yes" && (
          <TextField
            label="Provide an explanation"
            name="supplementalNeedsTrust"
            value={explanations.supplementalNeedsTrust || ""}
            onChange={handleExplanationChange}
            fullWidth
          />
        )} */}

        {/* Additional Question: Request for Authorization */}
        {safeNumber(parseCurrencyToNumber(globalData?.basicInformation?.bondAmount)) >= 250000 && (
          <>
            <Box
              sx={{
                border: "1px solid #d1d5db", // Light gray border
                borderRadius: "8px", // Rounded corners
                backgroundColor: "#f9fafb", // Light background
                padding: "16px", // Add spacing inside the box
                margin: "16px 0", // Add vertical spacing outside the box
              }}
            >
              <Typography variant="h6" sx={{ marginBottom: "2px" }}>
                Authorization to Obtain Underwriting Information
              </Typography>
              <Typography>
                Do you fully understand and agree, by submitting this request
                for approval you are providing authorization for Probate Bond
                Pros, surety companies and/or third-party brokers to verify
                information you provided as well as permission to obtain your
                consumer information from any source which would be necessary to
                determine bonding eligibility? This includes obtaining credit
                reports at time of application, during any review or renewal, at
                the time of any potential or actual claim, and/or for any other
                legitimate purposes determined by the surety in its reasonable
                discretion. This information will be held in the strictest
                confidence. Do you agree?
              </Typography>
            </Box>

            <TextField
              select
              label="Authorization to obtain underwriting information?"
              name="requestForAuthorization"
              value={data.requestForAuthorization || ""}
              onChange={handleChange}
              fullWidth
              required
            >
              <MenuItem value="" disabled>
                Select an option
              </MenuItem>
              <MenuItem value="Yes">Yes</MenuItem>
              <MenuItem value="No">No</MenuItem>
            </TextField>
          </>
        )}
      </Box>
    </Box>
  );
};

const GuardianForm = ({ data = { guardians: [] }, updateData, globalData, isGuest, errors = {} }) => {
  const [allCities, setAllCities] = useState([]);
  const [allStates, setAllStates] = useState([]);
  const [professionalGuardians, setProfessionalGuardians] = useState([]);
  const [loadingCitiesStates, setLoadingCitiesStates] = useState(true);
  const [loadingGuardians, setLoadingGuardians] = useState(false);
  const { role } = useSelector((state) => state.auth);
  const pgId = useSelector((state) => state.pg.pgId);
  const lawfirm = useSelector((state) => state.lawfirm);

  // Ref to track current data for async functions to avoid stale closures
  const dataRef = useRef(data);
  useEffect(() => {
    dataRef.current = data;
  }, [data]);

  // Professional Guardian name suggestion (like Probate/Guardianship)
  const NAME_SUGGESTION_DEBOUNCE_MS = 1000;
  const NAME_SUGGESTION_THRESHOLD = 0.92;
  const nameDebounceRefs = useRef([]);
  const [suggestedPG, setSuggestedPG] = useState([]);
  const [suggestionDialogOpen, setSuggestionDialogOpen] = useState([]);
  const [suggestionOptOut, setSuggestionOptOut] = useState([]);


  useEffect(() => {
    scrollToTop();
  }, []);

  useEffect(() => {
    if ((role === "professional guardian" && pgId) || (role === "pg_contact" && pgId)) {
      fetchProfessionalGuardianDetails(0, pgId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [role, pgId, professionalGuardians]);

  // Fetch cities and states for dropdowns
  useEffect(() => {
    const loadDropdownData = async () => {
      try {
        const cities = await apiClient("/fields/city/", "GET");
        const states = await apiClient("/fields/states/", "GET");
        setAllCities(cities);
        setAllStates(states);
        scrollToTop();
      } catch (error) {
        console.error("Error loading cities or states:", error);
      } finally {
        setLoadingCitiesStates(false);
      }
    };
    loadDropdownData();
  }, []);

  // Fetch Professional Guardians from API
  useEffect(() => {
    // Guest flow should not hit internal directory endpoints
    if (isGuest) {
      setProfessionalGuardians([]);
      setLoadingGuardians(false);
      return;
    }

    let cancelled = false;
    const fetchProfessionalGuardians = async () => {
      setLoadingGuardians(true);
      try {
        const response = await apiClient(
          "/get-all-professional-guardians",
          "GET"
        );
        const formattedGuardians = response.data.map((guardian) => ({
          value: guardian.pg_id,
          label: guardian.full_name,
          address:
            guardian.mailing_address === ", , ,"
              ? guardian.physical_address
              : guardian.mailing_address,
          cell: guardian.cell_phone,
        }));
        if (!cancelled) setProfessionalGuardians(formattedGuardians);
      } catch (error) {
        console.error("Error fetching professional guardians:", error);
      } finally {
        if (!cancelled) setLoadingGuardians(false);
      }
    };

    fetchProfessionalGuardians();
    return () => {
      cancelled = true;
    };
  }, [isGuest]);

  // Keep PG suggestion per-guardian arrays in sync with guardians length
  useEffect(() => {
    setSuggestedPG((prev) => data.guardians.map((_, i) => prev[i] || null));
    setSuggestionDialogOpen((prev) => data.guardians.map((_, i) => prev[i] || false));
    setSuggestionOptOut((prev) => data.guardians.map((_, i) => prev[i] || false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.guardians.length]);

  const handleGoogleCallback = (index, address, city, state, zipCode) => {
    // Use dataRef.current to get fresh state
    const currentData = dataRef.current;
    const updatedGuardians = Array.isArray(currentData.guardians) ? [...currentData.guardians] : [];
    updatedGuardians[index] = {
      ...updatedGuardians[index],
      address: address,
      city: city,
      state: state,
      zip_code: zipCode,
    };
    updateData({ ...currentData, guardians: updatedGuardians });
  };

  const handleZipCodeChange = async (e, index) => {
    const zipCode = e.target.value;
    const updatedGuardians = [...data.guardians];
    updatedGuardians[index] = { ...updatedGuardians[index], zip_code: zipCode };
    updateData({ ...data, guardians: updatedGuardians });

    // If ZIP code is 5 digits, fetch city and state
    if (zipCode.length === 5) {
      try {
        const response = await apiClient(`/fields/location/${zipCode}`, "GET");
        const { city, state } = response;
        // Use dataRef.current to get fresh state after async call
        const currentData = dataRef.current;
        const freshGuardians = Array.isArray(currentData.guardians) ? [...currentData.guardians] : [];
        freshGuardians[index] = {
          ...freshGuardians[index],
          city: city,
          state: state,
          zip_code: zipCode,
        };
        updateData({ ...currentData, guardians: freshGuardians });
      } catch (error) {
        console.error("Error fetching city/state by zip code:", error);
      }
    }
  };

  //Fetch details for one professional guardians
  const fetchProfessionalGuardianDetails = async (index, id) => {
    try {
      const response = await apiClient(`/guardian-details/${id}/`, "GET");
      // Use dataRef.current to get fresh state after async call
      const currentData = dataRef.current;
      const updatedGuardians = Array.isArray(currentData.guardians)
        ? [...currentData.guardians]
        : [];

      // Detect a malformed PG address (Kathleen-Hayes case). The backend
      // (common_utils/address_parser.py) returns null for any component it
      // cannot identify with confidence so we never silently inject shifted
      // values. Surface a clear toast so the user knows to update the PG
      // record before continuing.
      const missingAddressParts = [];
      if (!response.mailing_address) missingAddressParts.push("Street Address");
      if (!response.city) missingAddressParts.push("City");
      if (!response.state) missingAddressParts.push("State");
      if (!response.zip_code) missingAddressParts.push("ZIP/Postal Code");

      if (missingAddressParts.length > 0) {
        toast.warning(
          `${response.full_name || "Selected Professional Guardian"}'s address ` +
            `on file is incomplete (missing: ${missingAddressParts.join(", ")}). ` +
            `Please update the Professional Guardian record before continuing.`,
          { autoClose: 8000 }
        );
      }

      updatedGuardians[index] = {
        ...updatedGuardians[index],
        name: response.full_name,
        address: response.mailing_address,
        email: response.email,
        city: response.city,
        state: response.state,
        zip_code: response.zip_code,
        professional_guardian: id,
        preferred_billing: response.preferred_billing,
        is_professional_guardian: "Yes",
        is_new_pg: false,
        showFullForm: false,
        pg_address_raw: response.mailing_address_raw,
        pg_address_missing_parts: missingAddressParts,
      };
      updateData({ ...currentData, guardians: updatedGuardians });
    } catch (error) {
      console.error("Error fetching professional guardian details", error);
    }
  };

  const formatSSN = (ssn) => {
    if (ssn.length < 4) return ssn;
    return "XXX-XX-" + ssn.slice(-4);
  };

  // -------- Name similarity helpers for Professional Guardian suggestion --------
  const normalizeName = (str) => (str || "").toLowerCase().replace(/[^a-z\s]/g, "").replace(/\s+/g, " ").trim();

  const levenshteinDistance = (a, b) => {
    const s = normalizeName(a);
    const t = normalizeName(b);
    const n = s.length;
    const m = t.length;
    if (n === 0) return m;
    if (m === 0) return n;
    const d = Array.from({ length: n + 1 }, () => new Array(m + 1).fill(0));
    for (let i = 0; i <= n; i++) d[i][0] = i;
    for (let j = 0; j <= m; j++) d[0][j] = j;
    for (let i = 1; i <= n; i++) {
      for (let j = 1; j <= m; j++) {
        const cost = s[i - 1] === t[j - 1] ? 0 : 1;
        d[i][j] = Math.min(
          d[i - 1][j] + 1,
          d[i][j - 1] + 1,
          d[i - 1][j - 1] + cost
        );
      }
    }
    return d[n][m];
  };

  const similarityScore = (a, b) => {
    const sA = normalizeName(a);
    const sB = normalizeName(b);
    if (!sA || !sB) return 0;
    const dist = levenshteinDistance(sA, sB);
    let score = 1 - dist / Math.max(sA.length, sB.length);
    if (sA.includes(sB) || sB.includes(sA)) score = Math.max(score, 0.9);
    return score;
  };

  const getDebouncedCheckName = (idx) => {
    if (!nameDebounceRefs.current[idx]) {
      nameDebounceRefs.current[idx] = debounce((name) => {
        if (suggestionOptOut[idx]) {
          return;
        }
        const clean = normalizeName(name);
        if (!clean || clean.length < 4) {
          setSuggestionDialogOpen((prev) => {
            const arr = [...prev];
            arr[idx] = false;
            return arr;
          });
          return;
        }
        let best = null;
        let bestScore = 0;
        for (const pg of professionalGuardians) {
          const score = similarityScore(clean, pg.label);
          if (score > bestScore) {
            bestScore = score;
            best = pg;
          }
        }
        if (best && bestScore >= NAME_SUGGESTION_THRESHOLD) {
          setSuggestedPG((prev) => {
            const arr = [...prev];
            arr[idx] = best;
            return arr;
          });
          setSuggestionDialogOpen((prev) => {
            const arr = [...prev];
            arr[idx] = true;
            return arr;
          });
        } else {
          setSuggestionDialogOpen((prev) => {
            const arr = [...prev];
            arr[idx] = false;
            return arr;
          });
        }
      }, NAME_SUGGESTION_DEBOUNCE_MS);
    }
    return nameDebounceRefs.current[idx];
  };

  const handlePGSuggestionClose = (idx) => {
    setSuggestionDialogOpen((prev) => {
      const arr = [...prev];
      arr[idx] = false;
      return arr;
    });
  };

  const handlePGSuggestionDecline = (idx) => {
    setSuggestionOptOut((prev) => {
      const arr = [...prev];
      arr[idx] = true;
      return arr;
    });
    handlePGSuggestionClose(idx);
  };

  const handlePGSuggestionConfirm = (idx) => {
    const pg = suggestedPG[idx];
    if (!pg) return handlePGSuggestionClose(idx);
    const guardians = Array.isArray(data.guardians) ? [...data.guardians] : [];
    guardians[idx] = {
      ...guardians[idx],
      is_professional_guardian: "Yes",
      professional_guardian: pg.value,
      is_new_pg: false,
      showFullForm: false,
      is_attorney: "No",
      attorney: null,
    };
    updateData({ ...data, guardians });
    fetchProfessionalGuardianDetails(idx, pg.value);
    handlePGSuggestionClose(idx);
  };

  const renderGuardianFields = () => {
    const guardianFields = [];
    const numberOfGuardians = globalData.basicInformation?.PrincipalNumber || 1;

    // Ensure data.guardians is always an array
    const guardians = Array.isArray(data.guardians) ? data.guardians : [];

    for (let i = 0; i < numberOfGuardians; i++) {
      // Ensure guardian is initialized as an empty object if it does not exist at index i
      const guardian = guardians[i] || {};

      guardianFields.push(
        <Box key={`guardian${i}`} className="grid grid-cols-1 gap-4">
          {/* Dynamic Heading for Professional Guardian */}
          <Typography variant="h6" className="font-semibold mb-2">
            Principal #{i + 1}
          </Typography>

          {/* Is this Guardian a Professional Guardian? */}
          {!isGuest && (
            <TextField
              select
              label="Is Principal a Professional Guardian?"
              value={guardian.is_professional_guardian || "No"}
              onChange={(e) => {
                const value = e.target.value;
                const updatedGuardians = [...guardians];
                updatedGuardians[i] = {
                  ...updatedGuardians[i],
                  is_professional_guardian: value,
                  showFullForm: value === "Yes" ? guardian.showFullForm : false,
                  professional_guardian: null,
                  is_new_pg: false,
                  attorney: null,
                };
                updateData({ ...data, guardians: updatedGuardians });
              }}
              fullWidth
              required
            >
              <MenuItem value="Yes">Yes</MenuItem>
              <MenuItem value="No">No</MenuItem>
            </TextField>
          )}

          {/* Is this Guardian an Attorney? (only shown if not professional guardian) */}
          {!isGuest && !(guardian.is_professional_guardian === "Yes") && (
            <TextField
              select
              label="Is Principal an Attorney?"
              value={guardian.is_attorney || "No"}
              onChange={(e) => {
                const value = e.target.value;
                const updatedGuardians = [...guardians];
                updatedGuardians[i] = {
                  ...updatedGuardians[i],
                  is_attorney: value,
                  attorney: null,
                  showAttorneyForm: false,
                };
                updateData({ ...data, guardians: updatedGuardians });
              }}
              fullWidth
              required
            >
              <MenuItem value="Yes">Yes</MenuItem>
              <MenuItem value="No">No</MenuItem>
            </TextField>
          )}

          {/* Professional Guardian Autocomplete */}
          {guardian.is_professional_guardian === "Yes" &&
            !guardian.showFullForm ? (
            <Box sx={{ mt: 4 }}>
              <Autocomplete
                options={professionalGuardians}
                value={
                  professionalGuardians.find(
                    (pg) => pg.value === guardians[i].professional_guardian
                  ) || null
                }
                getOptionLabel={(option) => option.label || ""}
                loading={loadingGuardians}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Search Professional Guardian"
                    variant="outlined"
                    fullWidth
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {loadingGuardians ? (
                            <CircularProgress color="inherit" size={20} />
                          ) : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                onChange={(event, newValue) => {
                  if (newValue) {
                    const updatedGuardians = [...guardians];
                    updatedGuardians[i] = {
                      ...updatedGuardians[i],
                      professional_guardian: newValue.value,
                      is_new_pg: false,
                    };
                    fetchProfessionalGuardianDetails(i, newValue.value);
                    updateData({ ...data, guardians: updatedGuardians });
                  }
                }}
              />
              {guardian.is_professional_guardian === "Yes" &&
                !guardian.showFullForm &&
                (() => {
                  const pg = professionalGuardians.find(
                    (pg) => pg.value === guardian.professional_guardian
                  );
                  return pg ? (
                    <Box
                      sx={{
                        mt: 2,
                        p: 2,
                        border: "1px solid #e0e0e0",
                        borderRadius: 2,
                        backgroundColor: "#f9f9f9",
                        boxShadow: "0px 2px 5px rgba(0,0,0,0.1)",
                      }}
                    >
                      <Typography
                        variant="subtitle1"
                        sx={{ fontWeight: "bold", mb: 1 }}
                      >
                        Selected Professional Guardian:
                      </Typography>
                      <Typography variant="body1">
                        <strong>Name:</strong> {pg.label}
                      </Typography>
                      <Typography variant="body1">
                        <strong>Address:</strong> {pg.address}
                      </Typography>
                      <Typography variant="body1">
                        <strong>Cell Phone:</strong> {pg.cell}
                      </Typography>
                    </Box>
                  ) : null;
                })()}
              <Typography
                variant="body2"
                className="text-blue-500 cursor-pointer mt-2"
                onClick={() => {
                  const updatedGuardians = [...guardians];
                  updatedGuardians[i] = {
                    ...updatedGuardians[i],
                    showFullForm: true,
                    is_new_pg: true,
                  };
                  updateData({ ...data, guardians: updatedGuardians });
                }}
              >
                Professional Guardian not in the list?
              </Typography>
            </Box>
          ) : guardian.is_attorney === "Yes" && !guardian.showAttorneyForm ? (
            renderTrusteePrincipalAttorneySearch(
              { apiClient, role, lawfirmId: lawfirm.lawfirmId, toast, updateData, data, guardians },
              guardian,
              i,
              errors[`guardians.${i}.attorney`],
              ({ attorneys }) => (
                  <>
                      <Box sx={{ mb: 3, mt: 3 }}>
                        <Typography
                          variant="subtitle1"
                          sx={{ fontWeight: "bold", mb: 1 }}
                        >
                          Step 2: Address Information
                        </Typography>
                        <TextField
                          select
                          label="Use attorney's home address?"
                          value={guardian.is_attorney_home || "No"}
                          onChange={(e) =>
                            handleStandardPrincipalAttorneyHomeAddressChange(
                              updateData,
                              data,
                              i,
                              e.target.value,
                              attorneys
                            )
                          }
                          fullWidth
                          required
                        >
                          <MenuItem value="Yes">Yes</MenuItem>
                          <MenuItem value="No">No</MenuItem>
                        </TextField>
                      </Box>

                      {guardian.is_attorney_home === "Yes" && (
                        <Box className="grid grid-cols-1 gap-4">
                          <Box>
                            <Typography
                              sx={{
                                fontSize: "12px",
                                color: "rgba(0, 0, 0, 0.6)",
                                marginBottom: "4px",
                                fontWeight: 500,
                              }}
                            >
                              Search Address
                            </Typography>
                            <GoogleAutoComplete
                              placeholder="Search Address"
                              callback={(address, city, state, zipCode) =>
                                handleGoogleCallback(
                                  i,
                                  address,
                                  city,
                                  state,
                                  zipCode
                                )
                              }
                            />
                          </Box>

                          <TextField
                            sx={{ mt: 1, mb: 1 }}
                            label="Street Address"
                            value={guardian.address || ""}
                            onChange={(e) => {
                              const updatedGuardians = [...guardians];
                              updatedGuardians[i] = {
                                ...updatedGuardians[i],
                                address: e.target.value,
                              };
                              updateData({
                                ...data,
                                guardians: updatedGuardians,
                              });
                            }}
                            fullWidth
                            required
                          />

                          <TextField
                            label="ZIP/Postal Code"
                            value={guardian.zip_code || ""}
                            onChange={(e) => handleZipCodeChange(e, i)}
                            fullWidth
                            required
                          />

                          {loadingCitiesStates ? (
                            <Box display="flex" justifyContent="center" my={3}>
                              <CircularProgress />
                            </Box>
                          ) : (
                            <>
                              <LabeledCreatableSelect
                                label="City"
                                options={allCities.map((city) => ({
                                  label: city,
                                  value: city,
                                }))}
                                value={
                                  guardian.city
                                    ? {
                                      label: guardian.city,
                                      value: guardian.city,
                                    }
                                    : null
                                }
                                onChange={(opt) => {
                                  const updatedGuardians = [...guardians];
                                  updatedGuardians[i] = {
                                    ...updatedGuardians[i],
                                    city: opt ? opt.value : "",
                                  };
                                  updateData({
                                    ...data,
                                    guardians: updatedGuardians,
                                  });
                                }}
                                placeholder="Select or add a city"
                              />
                              <LabeledCreatableSelect
                                label="State"
                                options={allStates.map((state) => ({
                                  label: state,
                                  value: state,
                                }))}
                                value={
                                  guardian.state
                                    ? {
                                      label: guardian.state,
                                      value: guardian.state,
                                    }
                                    : null
                                }
                                onChange={(opt) => {
                                  const updatedGuardians = [...guardians];
                                  updatedGuardians[i] = {
                                    ...updatedGuardians[i],
                                    state: opt ? opt.value : "",
                                  };
                                  updateData({
                                    ...data,
                                    guardians: updatedGuardians,
                                  });
                                }}
                                placeholder="Select or add a state"
                              />
                            </>
                          )}
                        </Box>
                      )}
                  </>
                )
            )
          ) : (
            <Box className="grid grid-cols-1 gap-4">
              {/* Name of Guardian */}
              <TextField
                sx={{ mt: 2, mb: 2 }}
                label="Name"
                value={guardian.name || ""}
                onChange={(e) => {
                  const inputValue = e.target.value;
                  const updatedGuardians = [...guardians];
                  updatedGuardians[i] = {
                    ...updatedGuardians[i],
                    name: inputValue,
                    citizenship: "USA",
                    country: "USA",
                  };
                  updateData({ ...data, guardians: updatedGuardians });
                  if ((updatedGuardians[i].is_professional_guardian !== "Yes" || updatedGuardians[i].showFullForm) && !suggestionOptOut[i]) {
                    getDebouncedCheckName(i)(inputValue);
                  }
                }}
                fullWidth
                required
                inputProps={{ maxLength: 255 }}
              />

              {/* Professional Guardian Suggestion Dialog */}
              <Dialog
                open={suggestionDialogOpen[i] || false}
                onClose={(_, reason) => {
                  if (reason !== "backdropClick") handlePGSuggestionClose(i);
                }}
              >
                <DialogTitle>Similar Professional Guardian Found</DialogTitle>
                <DialogContent>
                  <Typography>
                    The name you entered is close to {suggestedPG[i]?.label}. Would you like to select {suggestedPG[i]?.label} as a Professional Guardian?
                  </Typography>
                </DialogContent>
                <DialogActions>
                  <Button onClick={() => handlePGSuggestionDecline(i)} color="secondary">No</Button>
                  <Button onClick={() => handlePGSuggestionConfirm(i)} color="primary" variant="contained">Yes, Select</Button>
                </DialogActions>
              </Dialog>

              {(guardian.is_new_pg || globalData.basicInformation?.is_esignatures === "Yes") && (
                <TextField
                  sx={{ mb: 2 }}
                  label="Email"
                  value={guardian.email || ""}
                  onChange={(e) => {
                    const updatedGuardians = [...guardians];
                    updatedGuardians[i] = {
                      ...updatedGuardians[i],
                      email: e.target.value,
                    };
                    updateData({ ...data, guardians: updatedGuardians });
                  }}
                  fullWidth
                  required
                />
              )}

              {/* Citizenship */}
              <Box>
                <TextField
                  select
                  label="Citizenship"
                  value={
                    guardian.citizenship === "Other"
                      ? "Other"
                      : guardian.citizenship || "USA"
                  }
                  onChange={(e) => {
                    const updatedGuardians = [...guardians];
                    if (e.target.value === "Other") {
                      updatedGuardians[i] = {
                        ...updatedGuardians[i],
                        citizenship: "Other",
                      };
                    } else {
                      updatedGuardians[i] = {
                        ...updatedGuardians[i],
                        citizenship: e.target.value,
                      };
                    }
                    updateData({ ...data, guardians: updatedGuardians });
                  }}
                  fullWidth
                  required
                >
                  <MenuItem value="USA">USA</MenuItem>
                  <MenuItem value="Canada">Canada</MenuItem>
                  <MenuItem value="UK">UK</MenuItem>
                  <MenuItem value="Mexico">Mexico</MenuItem>
                  <MenuItem value="Germany">Germany</MenuItem>
                  <MenuItem value="France">France</MenuItem>
                  <MenuItem value="Australia">Australia</MenuItem>
                  <MenuItem value="India">India</MenuItem>
                  <MenuItem value="Pakistan">Pakistan</MenuItem>
                  <MenuItem value="China">China</MenuItem>
                  <MenuItem value="Other">Other (Please specify)</MenuItem>
                </TextField>

                {guardian.citizenship === "Other" && (
                  <TextField
                    label="Specify Citizenship"
                    value={guardian.custom_citizenship || ""}
                    onChange={(e) => {
                      const updatedGuardians = [...guardians];
                      updatedGuardians[i] = {
                        ...updatedGuardians[i],
                        custom_citizenship: e.target.value,
                      };
                      updateData({ ...data, guardians: updatedGuardians });
                    }}
                    fullWidth
                    required
                    placeholder="Enter citizenship"
                  />
                )}
              </Box>

              {/* Country of Residence */}
              <Box>
                <TextField
                  select
                  label="Country of Residence"
                  value={
                    guardian.country === "Other"
                      ? "Other"
                      : guardian.country || "USA"
                  }
                  onChange={(e) => {
                    const updatedGuardians = [...guardians];
                    if (e.target.value === "Other") {
                      updatedGuardians[i] = {
                        ...updatedGuardians[i],
                        country: "Other",
                      };
                    } else {
                      updatedGuardians[i] = {
                        ...updatedGuardians[i],
                        country: e.target.value,
                      };
                    }
                    updateData({ ...data, guardians: updatedGuardians });
                  }}
                  fullWidth
                  required
                >
                  <MenuItem value="USA">USA</MenuItem>
                  <MenuItem value="Canada">Canada</MenuItem>
                  <MenuItem value="UK">UK</MenuItem>
                  <MenuItem value="Mexico">Mexico</MenuItem>
                  <MenuItem value="Germany">Germany</MenuItem>
                  <MenuItem value="France">France</MenuItem>
                  <MenuItem value="Australia">Australia</MenuItem>
                  <MenuItem value="India">India</MenuItem>
                  <MenuItem value="Pakistan">Pakistan</MenuItem>
                  <MenuItem value="China">China</MenuItem>
                  <MenuItem value="Other">Other (Please specify)</MenuItem>
                </TextField>

                {guardian.country === "Other" && (
                  <TextField
                    label="Specify Country"
                    value={guardian.custom_country || ""}
                    onChange={(e) => {
                      const updatedGuardians = [...guardians];
                      updatedGuardians[i] = {
                        ...updatedGuardians[i],
                        custom_country: e.target.value,
                      };
                      updateData({ ...data, guardians: updatedGuardians });
                    }}
                    fullWidth
                    required
                    placeholder="Enter country"
                  />
                )}
              </Box>

              {/* Relationship to Ward */}
              {!(guardian.is_professional_guardian === "Yes") && (
                <TextField
                  label="Relationship to Beneficiary"
                  value={guardian.relationship || ""}
                  onChange={(e) => {
                    const updatedGuardians = [...guardians];
                    updatedGuardians[i] = {
                      ...updatedGuardians[i],
                      relationship: e.target.value,
                    };
                    updateData({ ...data, guardians: updatedGuardians });
                  }}
                  fullWidth
                  required
                />
              )}

              {/* Google AutoComplete for Address */}
              <Box>
                <Typography
                  sx={{
                    fontSize: "12px",
                    color: "rgba(0, 0, 0, 0.6)",
                    marginBottom: "4px",
                    fontWeight: 500,
                  }}
                >
                  Search Address
                </Typography>
                <GoogleAutoComplete
                  placeholder="Search Address"
                  callback={(address, city, state, zipCode) =>
                    handleGoogleCallback(i, address, city, state, zipCode)
                  }
                />
              </Box>

              <TextField
                sx={{ mt: 1, mb: 1 }}
                label="Street Address"
                value={guardian.address || ""}
                onChange={(e) => {
                  const updatedGuardians = [...guardians];
                  updatedGuardians[i] = {
                    ...updatedGuardians[i],
                    address: e.target.value,
                  };
                  updateData({ ...data, guardians: updatedGuardians });
                }}
                fullWidth
                required
              />

              {/* ZIP Code */}
              <TextField
                label="ZIP/Postal Code"
                value={guardian.zip_code || ""}
                onChange={(e) => handleZipCodeChange(e, i)}
                fullWidth
                required
              />

              {/* City and State Dropdowns */}
              {loadingCitiesStates ? (
                <Box display="flex" justifyContent="center" my={3}>
                  <CircularProgress />
                </Box>
              ) : (
                <>
                  <LabeledCreatableSelect
                    label="City"
                    options={allCities.map((city) => ({
                      label: city,
                      value: city,
                    }))}
                    value={
                      guardian.city
                        ? { label: guardian.city, value: guardian.city }
                        : null
                    }
                    onChange={(opt) => {
                      const updatedGuardians = [...guardians];
                      updatedGuardians[i] = {
                        ...updatedGuardians[i],
                        city: opt ? opt.value : "",
                      };
                      updateData({ ...data, guardians: updatedGuardians });
                    }}
                    placeholder="Select or add a city"
                  />
                  <LabeledCreatableSelect
                    label="State"
                    options={allStates.map((state) => ({
                      label: state,
                      value: state,
                    }))}
                    value={
                      guardian.state
                        ? { label: guardian.state, value: guardian.state }
                        : null
                    }
                    onChange={(opt) => {
                      const updatedGuardians = [...guardians];
                      updatedGuardians[i] = {
                        ...updatedGuardians[i],
                        state: opt ? opt.value : "",
                      };
                      updateData({ ...data, guardians: updatedGuardians });
                    }}
                    placeholder="Select or add a state"
                  />
                </>
              )}

              {/* SSN and Occupation Fields */}
              {safeNumber(parseCurrencyToNumber(globalData.basicInformation?.bondAmount)) >= 250000 && (
                <TextField
                  label="Social Security Number (SSN)"
                  type="password"
                  value={guardian.ssn || ""}
                  onChange={(e) => {
                    const updatedGuardians = [...guardians];
                    updatedGuardians[i] = {
                      ...updatedGuardians[i],
                      ssn: e.target.value,
                    };
                    updateData({ ...data, guardians: updatedGuardians });
                  }}
                  fullWidth
                  required
                  helperText={formatSSN(guardian.ssn || "")}
                />
              )}

              {safeNumber(parseCurrencyToNumber(globalData.basicInformation?.bondAmount)) >= 250000 &&
                !(guardian.is_professional_guardian === "Yes") &&
                !(guardian.is_attorney === "Yes") && (
                  (() => {
                    const { employmentStatus, occupationText } =
                      splitEmploymentOccupation(guardian.occupation);

                    const questionLabel =
                      employmentStatus === "Retired"
                        ? "What occupation did you retire from?"
                        : employmentStatus === "Employed"
                          ? "What is your current occupation?"
                          : "Occupation";

                    return (
                      <>
              <TextField
                          select
                          label="Employment Status"
                          value={employmentStatus || ""}
                onChange={(e) => {
                            const nextStatus = e.target.value;
                            const nextOccupation = joinEmploymentOccupation(
                              nextStatus,
                              occupationText
                            );
                  const updatedGuardians = [...guardians];
                  updatedGuardians[i] = {
                    ...updatedGuardians[i],
                              occupation: nextOccupation,
                            };
                            updateData({ ...data, guardians: updatedGuardians });
                          }}
                          fullWidth
                          required
                        >
                          <MenuItem value="Employed">Employed</MenuItem>
                          <MenuItem value="Unemployed">Unemployed</MenuItem>
                          <MenuItem value="Retired">Retired</MenuItem>
                        </TextField>

                        {(employmentStatus === "Employed" ||
                          employmentStatus === "Retired") && (
                          <TextField
                            label={questionLabel}
                            value={occupationText || ""}
                            onChange={(e) => {
                              const nextText = e.target.value;
                              const nextOccupation = joinEmploymentOccupation(
                                employmentStatus,
                                nextText
                              );
                              const updatedGuardians = [...guardians];
                              updatedGuardians[i] = {
                                ...updatedGuardians[i],
                                occupation: nextOccupation,
                  };
                  updateData({ ...data, guardians: updatedGuardians });
                }}
                fullWidth
                required
              />
                        )}
                      </>
                    );
                  })()
                )}
            </Box>
          )}
        </Box>
      );
    }

    return guardianFields;
  };

  return (
    <Box
      className="bg-gray-100 p-6 rounded-lg shadow-md max-w-3xl mx-auto mt-8"
      sx={{ border: "1px solid #e0e0e0" }}
    >
      <Typography variant="h5" className="font-bold mb-4">
        Principals
      </Typography>

      {/* Render guardian fields */}
      {renderGuardianFields()}
    </Box>
  );
};

const AttorneyInformationForm = ({ globalData, data, updateData, errors, setErrors, isGuest }) => {
  const [attorneyInvolved, setAttorneyInvolved] = useState(
    data.attorneyInvolved || "Yes"
  );
  const [lawFirms, setLawFirms] = useState([]);
  const [lawFirmLocations, setLawFirmLocations] = useState([]);
  const [attorneys, setAttorneys] = useState([]);
  const [loadingLawFirms, setLoadingLawFirms] = useState(false);
  const [loadingLocations, setLoadingLocations] = useState(false);
  const [loadingAttorneys, setLoadingAttorneys] = useState(false);

  const [openAttorneyDialog, setOpenAttorneyDialog] = useState(false);
  const [openLawFirmDialog, setOpenLawFirmDialog] = useState(false);
  const [openAddressDialog, setOpenAddressDialog] = useState(false);
  // Initialize from `data`, preventing null overwrites
  const [lawFirm, setLawFirm] = useState(data.lawFirm || null);
  const [lawFirmAddress, setLawFirmAddress] = useState(
    data.lawFirmAddress || null
  );
  const [attorneyOfRecord, setAttorneyOfRecord] = useState(
    data.attorneyOfRecord || null
  );

  const [newAttorney, setNewAttorney] = useState({ name: "", phone: "" });
  const [newLawFirm, setNewLawFirm] = useState({ name: "", phone: "" });
  const [newAddress, setNewAddress] = useState({
    address: "",
    zip: "",
    city: "",
    county: "",
    state: "",
    phone: "",
  });
  const [fuzzyMatchDialogOpen, setFuzzyMatchDialogOpen] = useState(false);
  const [fuzzyMatchResult, setFuzzyMatchResult] = useState(null);
  const [fuzzyMatchLocationDialogOpen, setFuzzyMatchLocationDialogOpen] = useState(false);
  const [fuzzyMatchLocationResult, setFuzzyMatchLocationResult] = useState(null);

  const { role } = useSelector((state) => state.auth);
  const lawfirm = useSelector((state) => state.lawfirm);
  const pgId = useSelector((state) => state.pg.pgId);

  useEffect(() => {
    if (role === "professional guardian" && pgId) {
      setAttorneyInvolved("No");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    updateData({ ...data, attorneyInvolved: attorneyInvolved });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attorneyInvolved]);

  useEffect(() => {
    if (isGuest) {
      updateData({
        ...data,
        lawFirm: null,
        lawFirmAddress: null,
        attorneyOfRecord: null,
        attorneyInvolved: "Yes",
        guest_lawfirm_name: globalData.attorneyInformation.guest_lawfirm_name || "",
        guest_lawfirmaddress_name: globalData.attorneyInformation.guest_lawfirmaddress_name || "",
        guest_attorney_name: globalData.attorneyInformation.guest_attorney_name || "",
      });
      return;
    }
    fetchLocationsByLawFirm(lawFirm);
    fetchAttorneysByLawFirm(lawFirm);
    updateData({
      ...data,
      lawFirm: lawFirm,
      lawFirmAddress: lawFirmAddress,
      attorneyOfRecord: attorneyOfRecord,
      attorneyInvolved: attorneyInvolved,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lawFirm, lawFirmAddress, attorneyOfRecord, isGuest]);

  useEffect(() => {
    if (!isGuest) {
      fetchLawFirms();
    }
    scrollToTop();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isGuest]);

  const fetchLawFirms = async () => {
    setLoadingLawFirms(true);
    try {
      const response = await apiClient("/list-lawfirms/", "GET");
      const formattedLawFirms = response.map((firm) => ({
        label: firm.name,
        value: firm.lawfirm_id,
      }));
      setLawFirms(formattedLawFirms);
      if (role === "law_firm_user" || role === "law_firm_admin") {
        setAttorneyInvolved("Yes");
        setLawFirm(lawfirm.lawfirmId); // Sets selected law firm
        setLawFirmAddress(lawfirm.locationId); // Sets selected address (location)
        setAttorneyOfRecord(lawfirm.lawfirmUserId); // Sets attorney for this law firm
      }
    } catch (error) {
      console.error("Error fetching law firms:", error);
    } finally {
      setLoadingLawFirms(false);
    }
  };

  const fetchLocationsByLawFirm = async (lawFirmId) => {
    setLoadingLocations(true);
    try {
      const response = await apiClient(
        `/get-locations-by-lawfirm/${lawFirmId}`,
        "GET"
      );
      const formattedLocations = response.map((location) => ({
        label: location.name + " - " + location.address,
        value: location.law_firm_location_id,
        address: location.address,
      }));
      setLawFirmLocations(formattedLocations);
      if (data.lawFirmAddress) {
        return;
      } else if (formattedLocations.length === 1) {
        // If there is only one attorney, auto-select it
        const selectedLawFirm = formattedLocations[0];
        if (selectedLawFirm) {
          setLawFirmAddress(selectedLawFirm.value);
        }
      } else if (formattedLocations.length > 1) {
        // If there are multiple attorneys, auto-select the first one marked as `is_default: true
        const mainLocation = formattedLocations.find(
          (location) => location.label === "Main Location"
        );
        if (mainLocation) {
          setLawFirmAddress(mainLocation.value);
        }
      }
    } catch (error) {
      console.error("Error fetching law firm locations:", error);
    } finally {
      setLoadingLocations(false);
    }
  };

  const fetchAttorneysByLawFirm = async (lawFirmId) => {
    setLoadingAttorneys(true);
    try {
      const response = await apiClient(
        `/lawfirm-user-location/${lawFirmId}`,
        "GET"
      );
      const filteredAttorneys = response
        .filter((user) => user.Position === "Attorney")
        .map((attorney) => ({
          label: attorney.lawfirm_user_name,
          value: attorney.lawfirm_user_id,
          is_default: attorney.is_default,
        }));
      setAttorneys(filteredAttorneys);
      // Auto-select logic
      if (data.attorneyOfRecord) {
        return;
      } else if (filteredAttorneys.length === 1) {
        // If there is only one attorney, auto-select it
        const selectedAttorney = filteredAttorneys[0];
        if (selectedAttorney) {
          setAttorneyOfRecord(selectedAttorney.value);
        }
      } else if (filteredAttorneys.length > 1) {
        // If there are multiple attorneys, auto-select the first one marked as `is_default: true`
        const defaultAttorney = filteredAttorneys.find(
          (attorney) => attorney.is_default
        );
        if (defaultAttorney) {
          setAttorneyOfRecord(defaultAttorney.value);
        }
      }
    } catch (error) {
      console.error("Error fetching attorneys:", error);
    } finally {
      setLoadingAttorneys(false);
    }
  };

  const handleLawFirmChange = (event, newValue) => {
    if (newValue) {
      setLawFirm(newValue.value);
      fetchLocationsByLawFirm(newValue.value);
      fetchAttorneysByLawFirm(newValue.value);
      updateData({
        ...data,
        lawFirm: lawFirm,
        lawFirmAddress: lawFirmAddress,
        attorneyOfRecord: attorneyOfRecord,
        attorneyInvolved: attorneyInvolved,
      });
    } else {
      updateData({
        ...data,
        lawFirm: null,
        lawFirmAddress: null,
        attorneyOfRecord: null,
      });
      setLawFirmLocations([]);
      setAttorneys([]);
    }
  };

  const handleOpenAttorneyDialog = () => setOpenAttorneyDialog(true);
  const handleCloseAttorneyDialog = () => setOpenAttorneyDialog(false);
  const handleOpenLawFirmDialog = () => setOpenLawFirmDialog(true);
  const handleCloseLawFirmDialog = () => setOpenLawFirmDialog(false);
  const handleOpenAddressDialog = () => setOpenAddressDialog(true);
  const handleCloseAddressDialog = () => setOpenAddressDialog(false);

  const handleAddAttorney = async () => {
    try {
      setLoadingAttorneys(true);
      const response = await apiClient("/invite-lawfirm-user", "POST", {
        user: {
          email: `${newAttorney.name.toLowerCase().replaceAll(" ", "")}@test.com`,
        },
        law_firm_location_id: data.lawFirmAddress,
        lawfirm_id: data.lawFirm,
        lawfirm_user_name: newAttorney.name,
        preferred_name: newAttorney.name,
        title: "Attorney",
        position: "Attorney",
        is_legal_service: false,
        is_admin: false,
        middle_name: newAttorney.name,
        phone: newAttorney.phone,
        is_active: true,
      });
      updateData({ ...data, attorneyOfRecord: response.lawfirm_user_id });
      toast.success("Attorney added successfully!");
      handleCloseAttorneyDialog();
      fetchAttorneysByLawFirm(data.lawFirm); // Re-fetch attorneys and select the new one
    } catch (error) {
      console.error("Error adding attorney:", error);
      toast.error("Failed to add attorney!");
    } finally {
      setLoadingAttorneys(false);
    }
  };

  const handleAddLawFirm = async () => {
    try {
      setLoadingLawFirms(true);
      const fuzzyRes = await apiClient("/fuzzy-match-lawfirm", "POST", {
        name: newLawFirm.name,
      });
      const matchedId = fuzzyRes?.match?.lawfirm_id ?? fuzzyRes?.law_firm?.id ?? fuzzyRes?.lawfirm_id;
      const matchedName = fuzzyRes?.match?.name ?? fuzzyRes?.law_firm?.name ?? fuzzyRes?.law_firm_name ?? "";
      const hasMatch = (fuzzyRes?.match != null && matchedId != null) || (fuzzyRes?.matched && matchedId != null);
      if (hasMatch) {
        setLoadingLawFirms(false);
        setFuzzyMatchResult({ id: matchedId, name: matchedName || newLawFirm.name });
        setFuzzyMatchDialogOpen(true);
        return;
      }
      const response = await apiClient("/create-a-lawfirm", "POST", {
        name: newLawFirm.name,
        website: "",
        logo: "",
        accounting_contact_full_name: "",
        accounting_contact_email: "",
        accounting_contact_phone: "",
        is_new: true,
        is_active: true,
        created_by_guest: true,
      });
      updateData({ ...data, lawFirm: response.lawfirm_id });
      toast.success("Law Firm added successfully!");
      handleCloseLawFirmDialog();
      fetchLawFirms();
    } catch (error) {
      console.error("Error adding law firm:", error);
      toast.error("Failed to add law firm!");
    } finally {
      setLoadingLawFirms(false);
    }
  };

  const handleUseExistingLawFirm = () => {
    if (!fuzzyMatchResult) return;
    updateData({ ...data, lawFirm: fuzzyMatchResult.id });
    fetchLawFirms();
    setFuzzyMatchDialogOpen(false);
    setFuzzyMatchResult(null);
    handleCloseLawFirmDialog();
    toast.success("Using existing law firm.");
  };

  const handleCreateNewLawFirmAnyway = async () => {
    setFuzzyMatchDialogOpen(false);
    setFuzzyMatchResult(null);
    try {
      setLoadingLawFirms(true);
      const response = await apiClient("/create-a-lawfirm", "POST", {
        name: newLawFirm.name,
        website: "",
        logo: "",
        accounting_contact_full_name: "",
        accounting_contact_email: "",
        accounting_contact_phone: "",
        is_new: true,
        is_active: true,
        created_by_guest: true,
      });
      updateData({ ...data, lawFirm: response.lawfirm_id });
      toast.success("Law Firm added successfully!");
      handleCloseLawFirmDialog();
      fetchLawFirms();
    } catch (error) {
      console.error("Error adding law firm:", error);
      toast.error("Failed to add law firm!");
    } finally {
      setLoadingLawFirms(false);
    }
  };

  const doPostNewLocation = async () => {
    const response = await apiClient("/lawfirm-location/", "POST", [
      {
        lawfirm_id: data.lawFirm,
        name: "Guest Location",
        address: newAddress.address,
        zip_code: newAddress.zip,
        city: newAddress.city,
        county: newAddress.county,
        state: newAddress.state,
        phone: newAddress.phone,
        is_active: true,
      },
    ]);
    updateData({ ...data, lawFirmAddress: response[0].law_firm_location_id });
    toast.success("Address added successfully!");
    handleCloseAddressDialog();
    fetchLocationsByLawFirm(data.lawFirm);
  };

  const handleAddAddress = async () => {
    try {
      setLoadingLocations(true);
      const fuzzyRes = await apiClient("/fuzzy-match-lawfirm-location", "POST", {
        lawfirm_id: data.lawFirm,
        address: newAddress.address,
        city: newAddress.city,
        state: newAddress.state,
        zip_code: newAddress.zip,
        county: newAddress.county || "",
        country: "USA",
      });
      const matchedId = fuzzyRes?.match?.law_firm_location_id;
      const hasMatch = fuzzyRes?.match != null && matchedId != null;
      if (hasMatch) {
        setFuzzyMatchLocationResult({ law_firm_location_id: matchedId, score: fuzzyRes?.score ?? 0 });
        setFuzzyMatchLocationDialogOpen(true);
        setLoadingLocations(false);
        return;
      }
      await doPostNewLocation();
    } catch (error) {
      console.error("Error adding address:", error);
      toast.error("Failed to add address!");
    } finally {
      setLoadingLocations(false);
    }
  };

  const handleUseExistingLocation = () => {
    if (!fuzzyMatchLocationResult) return;
    updateData({ ...data, lawFirmAddress: fuzzyMatchLocationResult.law_firm_location_id });
    fetchLocationsByLawFirm(data.lawFirm);
    handleCloseAddressDialog();
    setFuzzyMatchLocationDialogOpen(false);
    setFuzzyMatchLocationResult(null);
    toast.success("Using existing location.");
  };

  const handleCreateNewLocationAnyway = async () => {
    setFuzzyMatchLocationDialogOpen(false);
    setFuzzyMatchLocationResult(null);
    try {
      setLoadingLocations(true);
      await doPostNewLocation();
    } catch (error) {
      console.error("Error adding address:", error);
      toast.error("Failed to add address!");
    } finally {
      setLoadingLocations(false);
    }
  };

  return (
    <Box
      className="bg-gray-100 p-6 rounded-lg shadow-md max-w-3xl mx-auto mt-8"
      sx={{ border: "1px solid #e0e0e0" }}
    >
      <Typography variant="h5" className="font-bold mb-4">
        Attorney Information
      </Typography>

      {!isGuest && (
        <FormControl component="fieldset" sx={{ mb: 4 }}>
          <Typography variant="body1">
            Will an attorney remain involved throughout the duration of the case?
          </Typography>
          <RadioGroup
            row
            name="attorneyInvolved"
            value={data.attorneyInvolved}
            onChange={(e) => setAttorneyInvolved(e.target.value)}
          >
            <FormControlLabel value="Yes" control={<Radio />} label="Yes" />
            <FormControlLabel value="No" control={<Radio />} label="No" />
          </RadioGroup>
        </FormControl>
      )}

      {isGuest ? (
        <Box className="grid grid-cols-1 gap-4">
          <TextField
            label="Law Firm Name"
            name="guest_lawfirm_name"
            value={data.guest_lawfirm_name || ""}
            onChange={(e) => updateData({ ...data, guest_lawfirm_name: e.target.value })}
            fullWidth
          />
          <TextField
            label="Law Firm Address"
            name="guest_lawfirmaddress_name"
            value={data.guest_lawfirmaddress_name || ""}
            onChange={(e) => updateData({ ...data, guest_lawfirmaddress_name: e.target.value })}
            fullWidth
          />
          <TextField
            label="Attorney Name"
            name="guest_attorney_name"
            value={data.guest_attorney_name || ""}
            onChange={(e) => updateData({ ...data, guest_attorney_name: e.target.value })}
            fullWidth
          />
        </Box>
      ) : attorneyInvolved === "Yes" && (
        <Box className="grid grid-cols-1 gap-4">
          {/* Law Firm Autocomplete */}
          <Autocomplete
            options={lawFirms}
            getOptionLabel={(option) => option.label || ""}
            loading={loadingLawFirms}
            value={lawFirms.find((firm) => firm.value === data.lawFirm) || null}
            onChange={handleLawFirmChange}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Law Firm"
                variant="outlined"
                fullWidth
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loadingLawFirms ? (
                        <CircularProgress color="inherit" size={20} />
                      ) : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
          <Typography
            variant="body2"
            className="text-blue-500 cursor-pointer -mt-2"
            sx={{ textAlign: "right" }}
            onClick={handleOpenLawFirmDialog}
          >
            Law firm not in the list?
          </Typography>

          {/* Law Firm Locations */}
          <Autocomplete
            options={lawFirmLocations}
            getOptionLabel={(option) => option.label || ""}
            loading={loadingLocations}
            value={
              lawFirmLocations.find(
                (loc) => loc.value === data.lawFirmAddress
              ) || null
            }
            onChange={(event, newValue) => {
              updateData({
                ...data,
                lawFirmAddress: newValue ? newValue.value : "",
              });
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Law Firm Address"
                variant="outlined"
                fullWidth
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loadingLocations ? (
                        <CircularProgress color="inherit" size={20} />
                      ) : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
          <Typography
            variant="body2"
            className="text-blue-500 cursor-pointer -mt-2"
            sx={{ textAlign: "right" }}
            onClick={handleOpenAddressDialog}
          >
            Address not in the list?
          </Typography>

          {/* Attorney of Record */}
          <Autocomplete
            options={attorneys}
            getOptionLabel={(option) => option.label || ""}
            loading={loadingAttorneys}
            value={
              attorneys.find(
                (attorney) => attorney.value === data.attorneyOfRecord
              ) || null
            }
            onChange={(event, newValue) => {
              updateData({
                ...data,
                attorneyOfRecord: newValue ? newValue.value : "",
              });
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Attorney of Record"
                variant="outlined"
                fullWidth
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loadingAttorneys ? (
                        <CircularProgress color="inherit" size={20} />
                      ) : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
          <Typography
            variant="body2"
            className="text-blue-500 cursor-pointer -mt-2"
            sx={{ textAlign: "right" }}
            onClick={handleOpenAttorneyDialog}
          >
            Attorney not in the list?
          </Typography>
        </Box>
      )}

      {/* Dialogs for Adding New Attorney, Law Firm, and Address */}
      <Dialog
        open={openAttorneyDialog}
        onClose={handleCloseAttorneyDialog}
        fullWidth
      >
        <DialogTitle>Add New Attorney</DialogTitle>
        <DialogContent>
          <TextField
            label="Attorney Name"
            name="name"
            value={newAttorney.name}
            onChange={(e) =>
              setNewAttorney({ ...newAttorney, name: e.target.value })
            }
            fullWidth
            required
            margin="normal"
          />
          <TextField
            label="Phone"
            name="phone"
            value={newAttorney.phone}
            onChange={(e) =>
              setNewAttorney({ ...newAttorney, phone: e.target.value })
            }
            fullWidth
            required
            margin="normal"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseAttorneyDialog} color="secondary">
            Cancel
          </Button>
          <Button onClick={handleAddAttorney} color="primary">
            Add Attorney
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openLawFirmDialog}
        onClose={handleCloseLawFirmDialog}
        fullWidth
      >
        <DialogTitle>Add New Law Firm</DialogTitle>
        <DialogContent>
          <TextField
            label="Law Firm Name"
            name="name"
            value={newLawFirm.name}
            onChange={(e) =>
              setNewLawFirm({ ...newLawFirm, name: e.target.value })
            }
            fullWidth
            required
            margin="normal"
          />
          <TextField
            label="Phone"
            name="phone"
            value={newLawFirm.phone}
            onChange={(e) =>
              setNewLawFirm({ ...newLawFirm, phone: e.target.value })
            }
            fullWidth
            required
            margin="normal"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseLawFirmDialog} color="secondary">
            Cancel
          </Button>
          <Button onClick={handleAddLawFirm} color="primary">
            Add Law Firm
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={fuzzyMatchDialogOpen} onClose={() => { setFuzzyMatchDialogOpen(false); setFuzzyMatchResult(null); }}>
        <DialogTitle>Similar law firm found</DialogTitle>
        <DialogContent>
          <Typography>
            A similar law firm was found: <strong>{fuzzyMatchResult?.name}</strong>. Do you want to use this existing firm or create a new one?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleUseExistingLawFirm} color="primary">
            Use existing
          </Button>
          <Button onClick={handleCreateNewLawFirmAnyway} color="secondary">
            Create new anyway
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={fuzzyMatchLocationDialogOpen} onClose={() => { setFuzzyMatchLocationDialogOpen(false); setFuzzyMatchLocationResult(null); }}>
        <DialogTitle>Similar location found</DialogTitle>
        <DialogContent>
          <Typography>
            A similar location was found for this address{fuzzyMatchLocationResult?.score ? ` (match score: ${Number(fuzzyMatchLocationResult.score).toFixed(0)}%)` : ""}. Do you want to use this existing location or add a new one?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleUseExistingLocation} color="primary">
            Use existing
          </Button>
          <Button onClick={handleCreateNewLocationAnyway} color="secondary">
            Add new anyway
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openAddressDialog}
        onClose={handleCloseAddressDialog}
        fullWidth
      >
        <DialogTitle>Add New Address</DialogTitle>
        <DialogContent>
          <TextField
            label="Address"
            name="address"
            value={newAddress.address}
            onChange={(e) =>
              setNewAddress({ ...newAddress, address: e.target.value })
            }
            fullWidth
            required
            margin="normal"
          />
          <TextField
            label="ZIP"
            name="zip"
            value={newAddress.zip}
            onChange={(e) =>
              setNewAddress({ ...newAddress, zip: e.target.value })
            }
            fullWidth
            required
            margin="normal"
          />
          <TextField
            label="City"
            name="city"
            value={newAddress.city}
            onChange={(e) =>
              setNewAddress({ ...newAddress, city: e.target.value })
            }
            fullWidth
            required
            margin="normal"
          />
          <TextField
            label="County"
            name="county"
            value={newAddress.county}
            onChange={(e) =>
              setNewAddress({ ...newAddress, county: e.target.value })
            }
            fullWidth
            required
            margin="normal"
          />
          <TextField
            label="State"
            name="state"
            value={newAddress.state}
            onChange={(e) =>
              setNewAddress({ ...newAddress, state: e.target.value })
            }
            fullWidth
            required
            margin="normal"
          />
          <TextField
            label="Phone"
            name="phone"
            value={newAddress.phone}
            onChange={(e) =>
              setNewAddress({ ...newAddress, phone: e.target.value })
            }
            fullWidth
            required
            margin="normal"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseAddressDialog} color="secondary">
            Cancel
          </Button>
          <Button onClick={handleAddAddress} color="primary">
            Add Address
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

const RequestorInformationForm = ({ data, bondData, updateData, errors }) => {
  const userdetails = useSelector((state) => state.auth.user);
  const phoneNumber = useSelector((state) => state.auth);

  // Function to clean country code
  const cleanPhoneNumber = (phone) => {
    if (!phone) return "";
    return phone.replace(/^\+1/, "").replace(/\D/g, ""); // Removes "+1" and non-numeric characters
  };

  useEffect(() => {
    const hasExistingRequestor = data?.email || data?.firstNameReq || data?.lastNameReq;
    if (userdetails?.email && !hasExistingRequestor) {
      updateData({
        ...data,
        email: userdetails.email,
        firstNameReq: userdetails.first_name,
        lastNameReq: userdetails.last_name,
        phoneNumber:
          data.phoneNumber || cleanPhoneNumber(phoneNumber.phoneNumber),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    updateData({ ...data, [name]: value });
  };

  useEffect(() => {
    scrollToTop();
  }, []);

  return (
    <Box
      className="bg-gray-100 p-6 rounded-lg shadow-md max-w-3xl mx-auto mt-8"
      sx={{
        border: "1px solid #e0e0e0", // Light border for sleekness
      }}
    >
      {/* Heading */}
      <Typography variant="h5" className="font-bold mb-4">
        Requestor Information
      </Typography>

      <Box className="grid grid-cols-1 gap-4">
        <TextField
          label="First Name"
          name="firstNameReq"
          value={data.firstNameReq || ""}
          onChange={handleChange}
          fullWidth
          required
          error={!!errors.firstNameReq}
          helperText={errors.firstNameReq}
        />

        <TextField
          label="Last Name"
          name="lastNameReq"
          value={data.lastNameReq || ""}
          onChange={handleChange}
          fullWidth
          required
          error={!!errors.lastNameReq}
          helperText={errors.lastNameReq}
        />

        <TextField
          label="Email"
          name="email"
          value={data.email || ""}
          onChange={handleChange}
          fullWidth
          required
          error={!!errors.email}
          helperText={errors.email}
        />

        <InputMask
          mask="(999) 999-9999"
          value={data.phoneNumber || ""}
          onChange={handleChange}
        >
          {(inputProps) => (
            <TextField
              {...inputProps}
              label="Phone Number"
              name="phoneNumber"
              fullWidth
              required
              error={!!errors.phoneNumber}
              helperText={errors.phoneNumber}
            />
          )}
        </InputMask>
      </Box>
    </Box>
  );
};

// Function to upload a file with file_name and fetch blob_link from the API
const uploadToADLS = async (file) => {
  try {
    // Prepare form data for the API
    const formData = new FormData();
    formData.append("file", file);
    formData.append("file_name", file.name); // Add file_name explicitly

    // Use the apiClient for the API call
    const response = await apiClient(
      "/bond-file-upload/", // Endpoint
      "POST", // Method
      formData // Data (formData for POST requests)
    );

    // Extract the blob_link from the response
    if (!response.blob_link) {
      throw new Error("Blob link not returned from the server.");
    }

    return response.blob_link;
  } catch (error) {
    console.error("Error uploading file:", error);
    throw error;
  }
};

const DocumentUploadForm = ({ data, updateData, globalData, errors }) => {
  const [formValues, setFormValues] = useState({
    courtOrder: null,
    lastWill: null,
    otherDocument: null,
    courtOrderLink: "",
    courtOrderFileName: "",
    lastWillLink: "",
    lastWillFileName: "",
    otherDocumentLink: "",
    otherDocumentFileName: "",
    note: "",
  });

  const [loading, setLoading] = useState({
    courtOrder: false,
    lastWill: false,
    otherDocument: false,
  });

  // Sync form values with data prop when the component mounts or when `data` changes
  useEffect(() => {
    if (data) {
      setFormValues((prevState) => ({
        ...prevState,
        courtOrderLink: data.courtOrderLink || "",
        lastWillLink: data.lastWillLink || "",
        otherDocumentLink: data.otherDocumentLink || "",
        courtOrderFileName: data.courtOrderFileName || "",
        lastWillFileName: data.lastWillFileName || "",
        otherDocumentFileName: data.otherDocumentFileName || "",
        note: data.note || "",
      }));
    }
  }, [data]);

  // Handle file input change and automatically upload
  const handleFileChange = async (e) => {
    const { name, files } = e.target;
    const file = files[0];

    if (!file) return;

    // Set the file in state and start loading
    setFormValues((prevState) => ({ ...prevState, [name]: file }));
    setLoading((prevState) => ({ ...prevState, [name]: true }));

    try {
      // Upload to ADLS and get the document link
      const uploadedLink = await uploadToADLS(file);

      // Set both file link and file name in the state
      setFormValues((prevState) => ({
        ...prevState,
        [name]: file,
        [`${name}Link`]: uploadedLink,
        [`${name}FileName`]: file.name, // Store the file name
      }));

      // Update the updatedData with the new form values
      updateData({
        ...data,
        [`${name}Link`]: uploadedLink,
        [`${name}FileName`]: file.name,
      });
    } catch (error) {
      console.error("Error uploading file:", error);
    } finally {
      setLoading((prevState) => ({ ...prevState, [name]: false }));
    }
  };

  const [removeConfirmOpen, setRemoveConfirmOpen] = useState(false);
  const [pendingRemoveName, setPendingRemoveName] = useState(null);

  const handleRemoveClick = (name) => {
    setPendingRemoveName(name);
    setRemoveConfirmOpen(true);
  };

  const handleRemoveConfirm = () => {
    if (pendingRemoveName) {
      setFormValues((prevState) => ({
        ...prevState,
        [pendingRemoveName]: null,
        [`${pendingRemoveName}Link`]: "",
        [`${pendingRemoveName}FileName`]: "",
      }));
      updateData({
        ...data,
        [`${pendingRemoveName}Link`]: "",
        [`${pendingRemoveName}FileName`]: "",
      });
    }
    setRemoveConfirmOpen(false);
    setPendingRemoveName(null);
  };

  const handleRemoveCancel = () => {
    setRemoveConfirmOpen(false);
    setPendingRemoveName(null);
  };

  // Handle text input changes (for note and other fields)
  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormValues((prevState) => {
      const updatedFormValues = { ...prevState, [name]: value };

      // Update the note or any other field in updatedData immediately after it changes
      updateData(updatedFormValues);

      return updatedFormValues;
    });
  };

  useEffect(() => {
    // Scroll to top or perform any other necessary side-effects
    window.scrollTo(0, 0);
  }, []);

  return (
    <Box
      className="bg-gray-100 p-6 rounded-lg shadow-md max-w-3xl mx-auto mt-8"
      sx={{
        border: "1px solid #e0e0e0", // Light border for sleekness
      }}
    >
      {/* Heading */}
      <Typography variant="h5" className="font-bold mb-4">
        Document Upload
      </Typography>

      <Box className="grid grid-cols-1 gap-6">
        {/* Court Order Document */}
        <Box>
          <Typography variant="body1" className="mb-2">
            Court File
          </Typography>
          <Button variant="outlined" component="label" fullWidth>
            Browse...
            <input
              type="file"
              hidden
              name="courtOrder"
              accept=".pdf,application/pdf"
              onChange={handleFileChange}
            />
          </Button>
          <Typography variant="body2" color="textSecondary" mt={1}>
            {loading.courtOrder ? (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <CircularProgress size={16} />
                <span>Uploading...</span>
              </Box>
            ) : formValues.courtOrder ? (
              formValues.courtOrder.name
            ) : (
              data.courtOrderFileName || "No file selected."
            )}
          </Typography>
          <Typography variant="body2" color="textSecondary" mt={1}>
            {formValues.courtOrderLink || data.courtOrderLink ? (
              <a
                href={formValues.courtOrderLink || data.courtOrderLink}
                target="_blank"
                rel="noopener noreferrer"
              >
                View Document
              </a>
            ) : (
              "No document link available."
            )}
          </Typography>
          <Typography variant="body2" color="textSecondary" mt={1}>
            {formValues.courtOrderFileName ||
              (data.courtOrderFileName &&
                `File name: ${formValues.courtOrderFileName || data.courtOrderFileName
                }`)}
          </Typography>
          {(formValues.courtOrder || formValues.courtOrderLink || data.courtOrderLink) && (
            <Button
              variant="outlined"
              color="error"
              fullWidth
              onClick={() => handleRemoveClick("courtOrder")}
              sx={{ mt: 1 }}
            >
              Remove Attachment
            </Button>
          )}
        </Box>

        {/* Last Will Document */}
        {/* <Box>
          <Typography variant="body1" className="mb-2">
            Last Will & Testament (Optional)
          </Typography>
          <Button variant="outlined" component="label" fullWidth>
            Browse...
            <input
              type="file"
              hidden
              name="lastWill"
              onChange={handleFileChange}
            />
          </Button>
          <Typography variant="body2" color="textSecondary" mt={1}>
            {formValues.lastWill ? formValues.lastWill.name : data.lastWillFileName || "No file selected."}
          </Typography>
          <Button
            variant="contained"
            color="primary"
            fullWidth
            onClick={() => handleUpload("lastWill")}
            disabled={loading.lastWill || !formValues.lastWill}
            sx={{ mt: 2 }}
          >
            {loading.lastWill ? <CircularProgress size={24} color="inherit" /> : "Upload"}
          </Button>
          <Typography variant="body2" color="textSecondary" mt={1}>
            {formValues.lastWillLink || data.lastWillLink ? (
              <a href={formValues.lastWillLink || data.lastWillLink} target="_blank" rel="noopener noreferrer">
                View Document
              </a>
            ) : (
              "No document link available."
            )}
          </Typography>
          <Typography variant="body2" color="textSecondary" mt={1}>
            {formValues.lastWillFileName || data.lastWillFileName && `File name: ${formValues.lastWillFileName || data.lastWillFileName}`}
          </Typography>
        </Box> */}

        {/* Other Document */}
        <Box>
          <Typography variant="body1" className="mb-2">
            Supplemental Needs Trust (Optional)
          </Typography>
          <Button variant="outlined" component="label" fullWidth>
            Browse...
            <input
              type="file"
              hidden
              name="otherDocument"
              accept=".pdf,application/pdf"
              onChange={handleFileChange}
            />
          </Button>
          <Typography variant="body2" color="textSecondary" mt={1}>
            {loading.otherDocument ? (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <CircularProgress size={16} />
                <span>Uploading...</span>
              </Box>
            ) : formValues.otherDocument ? (
              formValues.otherDocument.name
            ) : (
              data.otherDocumentFileName || "No file selected."
            )}
          </Typography>
          <Typography variant="body2" color="textSecondary" mt={1}>
            {formValues.otherDocumentLink || data.otherDocumentLink ? (
              <a
                href={formValues.otherDocumentLink || data.otherDocumentLink}
                target="_blank"
                rel="noopener noreferrer"
              >
                View Document
              </a>
            ) : (
              "No document link available."
            )}
          </Typography>
          <Typography variant="body2" color="textSecondary" mt={1}>
            {formValues.otherDocumentFileName ||
              (data.otherDocumentFileName &&
                `File name: ${formValues.otherDocumentFileName || data.otherDocumentFileName
                }`)}
          </Typography>
          {(formValues.otherDocument || formValues.otherDocumentLink || data.otherDocumentLink) && (
            <Button
              variant="outlined"
              color="error"
              fullWidth
              onClick={() => handleRemoveClick("otherDocument")}
              sx={{ mt: 1 }}
            >
              Remove Attachment
            </Button>
          )}
        </Box>

        {/* Note Field */}
        <TextField
          label="Note"
          name="note"
          value={formValues.note || data.note || ""}
          onChange={handleChange}
          fullWidth
          multiline
          rows={4}
          variant="outlined"
          sx={{ mb: 3 }}
        />
      </Box>

      <Dialog open={removeConfirmOpen} onClose={handleRemoveCancel}>
        <DialogTitle>Remove Attachment</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to remove this document? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleRemoveCancel} color="primary">
            Cancel
          </Button>
          <Button onClick={handleRemoveConfirm} color="error" variant="contained">
            Remove
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

const VermostTrusteeBondRequestForm = () => {
  const [activeStep, setActiveStep] = useState(() => {
    const savedPayload = localStorage.getItem("resumedBondPayload");
    if (savedPayload) {
      try {
        const parsed = JSON.parse(savedPayload);
        let body = parsed.request_body;
        if (typeof body === "string") body = JSON.parse(body);
        if (body && typeof body.activeStep === 'number') return body.activeStep;
      } catch (e) { }
    }
    return 0;
  });
  const [isLoading, setIsLoading] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const navigate = useNavigate();
  const location = useLocation();
  const [isGuestTrusteeRequest] = useState(
    (location.pathname || "").startsWith("/vermost-trustee-bond-request-guest")
  );
  const [openCalculator, setOpenCalculator] = useState(false);
  const [openSupport, setOpenSupport] = useState(false);
  const { cache_id } = useParams();
  const [cacheId, setCacheId] = useState(cache_id || "");

  const [supportModalOpen, setSupportModalOpen] = useState(false);
  const [submissionError, setSubmissionError] = useState("");
  const [failedBondType, setFailedBondType] = useState("");

  useEffect(() => {
    console.log(globalState);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeStep]);

  const [globalState, setGlobalState] = useState(() => {
    // 1. Check for Resume Payload FIRST
    const savedResume = localStorage.getItem("resumedBondPayload");
    if (savedResume) {
      try {
        const parsed = JSON.parse(savedResume);
        let body = parsed.request_body;
        if (typeof body === "string") body = JSON.parse(body);
        if (body && body.state) return body.state;
        if (parsed && !parsed.request_body) return parsed; // Fallback
      } catch (e) { }
    }

    // 2. Default Initial State
    const savedState = localStorage.getItem("selectedState");
    const savedType = localStorage.getItem("selectedBondType");

    let stateLabel = "";
    let typeLabel = "";
    try { if (savedState) stateLabel = JSON.parse(savedState).label; } catch (e) { }
    try { if (savedType) typeLabel = JSON.parse(savedType).label; } catch (e) { }

    return {
      basicInformation: {
        state: stateLabel,
        bondType: typeLabel,
        is_esignatures: "No"
      },
      generalInformation: {},
      guardianDetails: { guardians: [] },
      attorneyInformation: {},
      obligeeInformation: {},
      requestorInformation: {},
      documentUpload: {},
      billingInformation: {},
    };
  });

  // Cleanup resume payload after initialization
  useEffect(() => {
    if (localStorage.getItem("resumedBondPayload")) {
      toast.info("Restored bond application from support request.");
      localStorage.removeItem("resumedBondPayload");
    }
  }, []);

  useEffect(() => {
    const loadCache = async () => {
      if (!cache_id) return;
      try {
        const data = await apiClient(`/bond-cache/${cache_id}/`);
        let body = data?.request_body;
        if (typeof body === "string") {
          try { body = JSON.parse(body); } catch (_) { }
        }
        if (body?.state) {
          setGlobalState((prev) => ({ ...prev, ...body.state }));
        }
        if (typeof body?.activeStep === "number") {
          setActiveStep(body.activeStep);
        }
      } catch (e) {
        toast.error("Failed to load saved progress");
      }
    };
    loadCache();
  }, [cache_id]);

  const userdetails = useSelector((state) => state.auth.user);
  const lawfirmdetails = useSelector((state) => state.lawfirm);
  const { role } = useSelector((state) => state.auth);


  const generatePayload = (globalState) => {
    return {
      bond_type: 4,
      bond_sub_category: globalState.basicInformation?.bondSubCategory,
      bond_amount: safeNumber(parseCurrencyToNumber(globalState.basicInformation?.bondAmount)),
      premium: globalState.basicInformation?.premiumAmount,
      court_case_number: globalState.basicInformation?.hasCourtCaseNumber === "Yes" ? globalState.basicInformation?.courtCaseNumber : "",
      state: 6,
      bond_carrier: is_prod ? 7 : 4,
      county: globalState.basicInformation?.county,
      is_esignatures: globalState.basicInformation?.is_esignatures === "Yes",
      is_guest: isGuestTrusteeRequest,
      ...(isGuestTrusteeRequest ? (() => {
        try { const r = JSON.parse(localStorage.getItem("guestReferralData")); return r ? { referral_source: r.referral_source || null, referred_by: r.referred_by || null } : {}; } catch { return {}; }
      })() : {}),
      status: "Active",
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      law_firm: isGuestTrusteeRequest ? null : globalState.attorneyInformation?.lawFirm,
      law_firm_location: isGuestTrusteeRequest ? null : globalState.attorneyInformation?.lawFirmAddress,
      law_firm_user: isGuestTrusteeRequest ? null : globalState.attorneyInformation?.attorneyOfRecord,
      guest_lawfirm_name: isGuestTrusteeRequest ? globalState.attorneyInformation?.guest_lawfirm_name : undefined,
      guest_lawfirmaddress_name: isGuestTrusteeRequest ? globalState.attorneyInformation?.guest_lawfirmaddress_name : undefined,
      guest_attorney_name: isGuestTrusteeRequest ? globalState.attorneyInformation?.guest_attorney_name : undefined,
      obligee: globalState.obligeeInformation?.obligeeId,
      requester_id: isGuestTrusteeRequest ? null : userdetails.id,
      managing_lawfirm:
        lawfirmdetails.lawfirmId || globalState.attorneyInformation?.lawFirm,
      requester_user_email: globalState.requestorInformation?.email,
      requester_phone_number: globalState.requestorInformation?.phoneNumber,
      name: globalState.basicInformation?.legalName,
      requester_name:
        globalState.requestorInformation?.firstNameReq +
        " " +
        globalState.requestorInformation?.lastNameReq,
      trustee_bond: {
        county: globalState.basicInformation?.county,
        amount: globalState.basicInformation?.bondAmount,
        court_file_no: globalState.basicInformation?.hasCourtCaseNumber === "Yes" ? globalState.basicInformation?.courtCaseNumber : "",
        legal_name: globalState.basicInformation?.legalName,
        trust_name: globalState.basicInformation?.trustName,
        dob: globalState.basicInformation?.dateOfBirth,
        number_of_principals: globalState.basicInformation?.PrincipalNumber,
        all_us_canadian_uk_citizens:
          globalState.generalInformation?.allUSUKCanada === "Yes",
        all_us_canadian_uk_citizens_explanation: "",
        any_business_operated:
          globalState.generalInformation?.ongoingBusiness === "Yes",
        any_business_operated_explanation:
          globalState.generalInformation?.explanations?.ongoingBusiness,
        indebted_state:
          globalState.generalInformation?.indebtedGuardians === "Yes",
        indebted_state_explanation:
          globalState.generalInformation?.explanations?.indebtedGuardians,
        is_replaced_prior:
          globalState.generalInformation?.replacingPriorBond === "Yes",
        is_replaced_prior_explanation:
          globalState.generalInformation?.explanations?.replacingPriorBond,
        is_supplemental_needs:
          globalState.generalInformation?.supplementalNeedsTrust === "Yes",
        copy_of_court_order: globalState.documentUpload?.courtOrderFileName,
        copy_of_court_order_link: globalState.documentUpload?.courtOrderLink,
        other_document: globalState.documentUpload?.otherDocumentFileName,
        other_document_link: globalState.documentUpload?.otherDocumentLink,
        billing_entity: globalState.billingInformation?.billing_entity,
        billing_lawfirmaddress_name:
          globalState.billingInformation?.billing_lawfirmaddress_name,
        billing_principal_name:
          globalState.billingInformation?.billing_principal_name,
        billing_other_name: globalState.billingInformation?.billing_other_name,
        billing_other_address:
          globalState.billingInformation?.billing_other_address,
        billing_other_relationship:
          globalState.billingInformation?.billing_other_relationship,
        billing_other_city:
          globalState.billingInformation?.billing_other_city || "",
        billing_other_state:
          globalState.billingInformation?.billing_other_state || "",
        billing_other_zip:
          globalState.billingInformation?.billing_other_zip || "",
        is_billing_override:
          globalState.billingInformation?.is_billing_override ?? false,
        credit_report_allowed:
          globalState.generalInformation?.requestForAuthorization === "Yes",
      },

      trustees: (globalState.guardianDetails?.guardians || []).slice(0, globalState.basicInformation?.PrincipalNumber),
    };
  };

  const validatePremium = (payload) => {
    const recalculatedPremium = calculatePremium(
      safeNumber(parseCurrencyToNumber(payload.bond_amount)),
      globalState.basicInformation?.dateOfBirth,
      globalState.basicInformation?.bondSubCategory,
      globalState.basicInformation?.bondTerm
    );
    if (recalculatedPremium !== payload.premium) {
      console.warn(`Premium mismatch: original ${payload.premium}, recalculated ${recalculatedPremium}`);
      payload.premium = recalculatedPremium;
    }
  };

  const handleSave = async () => {
    try {
      setIsSaving(true);
      const requester_id = userdetails?.id;
      if (!requester_id) {
        toast.error("Unable to save: Login to save your progress");
        setIsSaving(false);
        return;
      }

      const name = globalState?.basicInformation?.legalName;

      const saved = await apiClient(`/bond-cache/`, "POST", {
        cache_id: cacheId || cache_id,
        requester_id,
        bond_type: "Trustee",
        name: name || "Untitled Trustee Bond",
        request_body: JSON.stringify({
          activeStep,
          state: globalState,
        }),
      });

      const returnedId = saved?.bond_cache_id || saved?.cache_id || saved?.id;
      if (returnedId) {
        if (returnedId !== cacheId) setCacheId(returnedId);
        if (!cache_id) {
          navigate(`/vermost-trustee-bond-request-cache/${returnedId}`, { replace: true });
        }
      }

      toast.success("Progress saved");
    } catch (error) {
      toast.error(
        (error && (error.detail || error.error)) || "Failed to save progress"
      );
    } finally {
      setIsSaving(false);
    }
  };

  const handleFinish = async () => {
    setIsLoading(true);
    const payload = generatePayload(globalState);

    // Validate premium before submission
    validatePremium(payload);

    try {
      // Create bond
      const response = await apiClient(
        "/create_trustee_bond/",
        "POST",
        payload
      );


      if (cacheId || cache_id) {
        await apiClient(`/bond-cache/${cacheId || cache_id}/`, "DELETE");
      }

      setIsLoading(false);
      localStorage.removeItem("guestReferralData");

      toast.success("Bond created successfully");

      // Queue a cross-page success toast on the destination home
      try {
        const bondId =
          response?.bond_id ||
          response?.id ||
          response?.bond?.bond_id ||
          response?.data?.bond_id;
        if (bondId) {
          sessionStorage.setItem(
            "flashToast",
            JSON.stringify({
              message: `Your Recent Bond Request #${bondId} was Submitted!`,
              link: `/vermost-trustee-bond-update/${bondId}`,
            })
          );
        }
      } catch (_) { }

      // Reset state to initial values
      setActiveStep(0);
      setGlobalState({
        basicInformation: {},
        generalInformation: {},
        guardianDetails: { guardians: [] },
        attorneyInformation: {},
        requestorInformation: {},
        documentUpload: {},
        billingInformation: {},
      });
      setErrors({}); // Clear any existing errors
      const rolePathMap = {
        super_user: "/admin_home",
        internal_user: "/internal_user_home",
        law_firm_user: "/law_firm_user_home",
        law_firm_admin: "/law_firm_admin_home",
        carrier_team_member: "/carrier_team_member_dashboard",
        "professional guardian": "/professional_guardian_home",
        pg_contact: "/pg_contact_home",
      };
      navigate(rolePathMap[role]);

    } catch (error) {
      const message = extractErrorMessage(error);
      console.error("Error creating bond:", error);

      setSubmissionError(message);
      setFailedBondType("Trustee");
      setSupportModalOpen(true);

      setIsLoading(false);
    }
  };

  const handleSupportRequestSubmit = async (description) => {
    try {
      const hydrationPayload = {
        activeStep,
        state: globalState,
      };

      await createSupportRequest({
        request_body: JSON.stringify(hydrationPayload),
        bond_type: failedBondType,
        name: globalState?.basicInformation?.legalName || "Unknown",
        user_comment: description
      });
      toast.success("Support request created successfully.");
      setSupportModalOpen(false);

      const rolePathMap = {
        super_user: "/admin_home",
        internal_user: "/internal_user_home",
        law_firm_user: "/law_firm_user_home",
        law_firm_admin: "/law_firm_admin_home",
        carrier_team_member: "/carrier_team_member_dashboard",
        "professional guardian": "/professional_guardian_home",
        pg_contact: "/pg_contact_home",
      };
      navigate(rolePathMap[role] || "/");
    } catch (error) {
      console.error("Failed to create support request", error);
      toast.error("Failed to submit support request.");
    }
  };

  const handleDialogClose = () => {
    setIsDialogOpen(false);
  };

  const handleSignIndemnity = () => {
    console.log("Redirecting to indemnity signing page...");
    setIsDialogOpen(false);
  };

  const steps = [
    "Basic Information",
    "General Information",
    "Principal Information",
    "Attorney Information",
    "Obligee Information",
    "Requestor Information",
    "Document Upload",
    "Billing Information",
  ];

  const getRequiredFieldsForStep = (globalState, step) => {
    if (step === 3 && isGuestTrusteeRequest) {
      return [];
    }
    const baseFields = {
      0: [
        "county",
        "legalName",
        "trustName",
        "dateOfBirth",
        "bondAmount",
        "PrincipalNumber",
        "is_esignatures",
      ],
      1: [],
      2: [],
      3:
        globalState.attorneyInformation?.attorneyInvolved === "Yes"
          ? ["lawFirm", "lawFirmAddress", "attorneyOfRecord"]
          : [],
      4: ["obligeeId"],
      5: ["firstNameReq", "lastNameReq", "email", "phoneNumber"],
      6: [],
      7: ["billing_entity"],
    };
    let fields = [...(baseFields[step] || [])];
    // Require authorization when bond amount > 250,000 on General Information (step 1)
    if (
      step === 1 &&
      (globalState?.basicInformation?.bondAmount &&
        safeNumber(parseCurrencyToNumber(globalState.basicInformation.bondAmount)) >= 250000)
    ) {
      fields.push("requestForAuthorization");
    }
    return fields;
  };

  useEffect(() => {
    scrollToTop();
  }, [activeStep]);

  // the exact keys your GuardianForm uses for each guardian
  const guardianFieldNames = [
    // "is_professional_guardian",
    // "is_attorney",
    "name",
    "address",
    "zip_code",
    "city",
    "state",
    // 'citizenship',
    // 'relationship'
  ];

  const STEP_LIMITS_MAP = {
    0: TRUSTEE_FIELD_LIMITS.basicInformation,
    1: TRUSTEE_FIELD_LIMITS.generalInformation,
    2: TRUSTEE_FIELD_LIMITS.principalInformation,
    5: TRUSTEE_FIELD_LIMITS.requestorInformation,
    7: TRUSTEE_FIELD_LIMITS.billingInformation,
  };

  const validateStepData = (data, step) => {
    let requiredFields = getRequiredFieldsForStep(globalState, step) || [];
    const errors = {};

    // step 2: enforce N blocks of guardian fields based on PrincipalNumber
    if (step === 2) {
      // use your actual PrincipalNumber here
      const count = globalState.basicInformation?.PrincipalNumber || 1;
      const dynamicGuardianFields = [...guardianFieldNames];

      // Add email if esignatures is Yes
      if (globalState.basicInformation?.is_esignatures === "Yes") {
        dynamicGuardianFields.push("email");
      }

      // Build guardian fields array
      const guardianFields = [];
      for (let i = 0; i < count; i++) {
        dynamicGuardianFields.forEach((field) => {
          guardianFields.push(`guardians.${i}.${field}`);
        });
      }
      requiredFields = [...requiredFields, ...guardianFields];
    }

    // Add conditional fields for step 7 when billing_entity is 'Other'
    if (step === 7 && data?.billing_entity === "Other") {
      requiredFields = [
        ...requiredFields,
        "billing_other_name",
        "billing_other_address",
        "billing_other_city",
        "billing_other_state",
        "billing_other_zip",
      ];
    }

    requiredFields.forEach((field) => {
      const fieldParts = field.split(".");
      let value = data;

      for (let part of fieldParts) {
        if (!value[part]) {
          errors[field] = "This field is required";
          break;
        }
        value = value[part];
      }
    });

    // Field length validation against backend model constraints
    const lengthErrors = validateFieldLengths(data, STEP_LIMITS_MAP[step]);
    Object.assign(errors, lengthErrors);

    // Employment Status / Occupation validation (stored in `guardians[i].occupation`)
    if (step === 2) {
      const bondAmount = safeNumber(parseCurrencyToNumber(globalState.basicInformation?.bondAmount));
      if (bondAmount >= 250000) {
        const count = globalState.basicInformation?.PrincipalNumber || 1;
        for (let i = 0; i < count; i++) {
          const guardian = data.guardians?.[i] || {};
          // Only validate if not professional guardian and not attorney
          if (
            guardian.is_professional_guardian !== "Yes" &&
            guardian.is_attorney !== "Yes"
          ) {
            const { employmentStatus, occupationText } = splitEmploymentOccupation(
              guardian.occupation
            );
            if (!employmentStatus) {
              errors[`guardians.${i}.occupation`] = "Employment Status is required";
            } else if (
              (employmentStatus === "Employed" || employmentStatus === "Retired") &&
              !occupationText
            ) {
              errors[`guardians.${i}.occupation`] =
                employmentStatus === "Retired"
                  ? "What occupation did you retire from? is required"
                  : "What is your current occupation? is required";
            }
          }
        }
      }
    }

    return errors;
  };

  const handleNext = () => {
    const currentStepData = globalState[Object.keys(globalState)[activeStep]];
    const currentStepErrors = validateStepData(currentStepData, activeStep);

    if (Object.keys(currentStepErrors).length > 0) {
      setErrors(currentStepErrors);

      const lengthErrors = Object.entries(currentStepErrors)
        .filter(([, msg]) => msg.includes("characters or less"))
        .map(([, msg]) => msg);
      const requiredErrors = Object.entries(currentStepErrors)
        .filter(([, msg]) => !msg.includes("characters or less"));

      if (lengthErrors.length > 0) {
        lengthErrors.forEach((msg) => toast.warning(msg));
      }

      if (requiredErrors.length > 0) {
        // Step 2 (Principal Information): emit one toast per principal
        // listing exactly which fields are missing instead of the legacy
        // generic "Please fill all required principal fields" message.
        // See `Helpers/principalErrorMessages.js` for the full rationale.
        const principalErrorMessages =
          activeStep === 2
            ? buildPrincipalErrorMessages(currentStepErrors)
            : [];

        if (principalErrorMessages.length > 0) {
          principalErrorMessages.forEach((msg) => toast.error(msg));
        } else {
          const errorFields = requiredErrors
            .map(([field]) => field.replace(/([A-Z])/g, " $1"))
            .map((field) => field.replace(/\./g, " "))
            .map((field) => field.charAt(0).toUpperCase() + field.slice(1))
            .join(", ");
          toast.error(`Please complete the following fields: ${errorFields}`);
        }
      }
      return;
    }

    if (activeStep === steps.length - 1) {
      handleFinish();
    } else {
      setActiveStep((prevStep) => prevStep + 1);
      setErrors({}); // Clear previous step errors
    }
  };

  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
  };

  const handleStepClick = (stepIndex) => {
    // Allow moving back without validation
    if (stepIndex < activeStep) {
      setActiveStep(stepIndex);
      return;
    }

    // Validate before moving forward
    const currentStepData = globalState[Object.keys(globalState)[activeStep]];
    const currentStepErrors = validateStepData(currentStepData, activeStep);

    if (Object.keys(currentStepErrors).length > 0) {
      setErrors(currentStepErrors);

      // Extract list of fields with errors
      const errorFields = Object.keys(currentStepErrors)
        .map((field) => field.replace(/([A-Z])/g, " $1")) // Split camelCase into words
        .map((field) => field.replace(/\./g, " ")) // Replace dot notation with space
        .map((field) => field.charAt(0).toUpperCase() + field.slice(1)) // Capitalize first letter
        .join(", ");

      toast.error(`Please complete the following fields: ${errorFields}`);
      return;
    }

    // Move to selected step if validation passes
    setActiveStep(stepIndex);
    setErrors({});
  };

  const updateGlobalState = (stepKey, stepData) => {
    setGlobalState((prevState) => ({
      ...prevState,
      [stepKey]: stepData,
    }));
    // Clear errors for only the fields that exist in the stepData
    Object.keys(stepData).forEach((fieldName) => {
      setErrors((prevErrors) => ({
        ...prevErrors,
        [fieldName]: "",
      }));
    });
  };

  return (
    <>
      {isGuestTrusteeRequest ? <GuestNavbar /> : <Navbar />}
      <Box className="p-6 max-w-4xl mx-auto" sx={{ pb: isGuestTrusteeRequest ? 10 : 0 }}>
        <Typography
          variant="h4"
          align="center"
          gutterBottom
          sx={{ fontWeight: "bold" }}
        >
          Trustee Bond Request for State of{" "}
          {JSON.parse(localStorage.getItem("selectedState")).label}
        </Typography>

        <Stepper activeStep={activeStep} className="mb-6 mt-6">
          {steps.map((label, index) => (
            <Step key={index}>
              <StepLabel
                onClick={() => handleStepClick(index)}
                style={{ cursor: "pointer" }}
              >
                {label}
              </StepLabel>
            </Step>
          ))}
        </Stepper>
        <Box className="flex justify-between items-center">
          <Button
            disabled={activeStep === 0}
            onClick={handleBack}
            className="mr-4"
          >
            Back
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleNext}
            disabled={isLoading}
            endIcon={isLoading && <CircularProgress size={20} />}
          >
            {isLoading
              ? "Processing..."
              : activeStep === steps.length - 1
                ? "Submit"
                : "Next"}
          </Button>
        </Box>

        <Box className="mb-6">
          {activeStep === 0 && (
            <BasicInformationForm
              globalData={globalState}
              data={globalState.basicInformation}
              updateData={(data) => updateGlobalState("basicInformation", data)}
              errors={errors}
              setErrors={setErrors}
            />
          )}
          {activeStep === 1 && (
            <GeneralInformationForm
              globalData={globalState}
              data={globalState.generalInformation}
              updateData={(data) =>
                updateGlobalState("generalInformation", data)
              }
              errors={errors}
              setErrors={setErrors}
            />
          )}
          {activeStep === 2 && (
            <GuardianForm
              data={globalState.guardianDetails}
              globalData={globalState}
              updateData={(data) => updateGlobalState("guardianDetails", data)}
              errors={errors}
              setErrors={setErrors}
              isGuest={isGuestTrusteeRequest}
            />
          )}
          {activeStep === 3 && (
            <AttorneyInformationForm
              globalData={globalState}
              data={globalState.attorneyInformation}
              updateData={(data) =>
                updateGlobalState("attorneyInformation", data)
              }
              errors={errors}
              setErrors={setErrors}
              isGuest={isGuestTrusteeRequest}
            />
          )}

          {activeStep === 4 && (
            <ObligeeInformationForm
              globalData={globalState}
              data={globalState.obligeeInformation}
              updateData={(data) =>
                updateGlobalState("obligeeInformation", data)
              }
              errors={errors}
              setErrors={setErrors}
            />
          )}

          {activeStep === 5 && (
            <RequestorInformationForm
              globalData={globalState}
              data={globalState.requestorInformation}
              updateData={(data) =>
                updateGlobalState("requestorInformation", data)
              }
              errors={errors}
              setErrors={setErrors}
            />
          )}

          {activeStep === 6 && (
            <DocumentUploadForm
              globalData={globalState}
              data={globalState.documentUpload}
              updateData={(data) => updateGlobalState("documentUpload", data)}
              errors={errors}
              setErrors={setErrors}
            />
          )}
          {activeStep === 7 && (
            <BillingInformationForm
              globalData={globalState}
              data={globalState.billingInformation}
              updateData={(data) =>
                updateGlobalState("billingInformation", data)
              }
              errors={errors}
              setErrors={setErrors}
              isGuest={isGuestTrusteeRequest}
            />
          )}
        </Box>

        <Box className="flex justify-between">
          <Button
            disabled={activeStep === 0}
            onClick={handleBack}
            className="mr-4"
          >
            Back
          </Button>
          <Button
            variant="outlined"
            color="secondary"
            onClick={handleSave}
            className="mx-2"
            endIcon={isSaving && <CircularProgress size={20} />}
            disabled={isSaving}
          >
            {isSaving ? "Saving..." : "Save"}
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleNext}
            disabled={isLoading}
            endIcon={isLoading && <CircularProgress size={20} />}
          >
            {isLoading
              ? "Processing..."
              : activeStep === steps.length - 1
                ? "Submit"
                : "Next"}
          </Button>
        </Box>
      </Box>

      {/* Dialog */}
      <Dialog open={isDialogOpen} onClose={handleDialogClose}>
        <DialogTitle>Bond Submission</DialogTitle>
        <DialogContent>
          <Box display="flex" alignItems="center" mb={2}>
            <CheckCircle color="success" sx={{ mr: 2 }} />
            <Typography variant="body1">
              The bond was successfully processed.
            </Typography>
          </Box>
          <Box display="flex" alignItems="center">
            <WarningAmber color="warning" sx={{ mr: 2 }} />
            <Typography variant="body1">
              The bond will not be forwarded until the indemnity agreement is
              signed.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose} color="secondary">
            Close
          </Button>
          <Button
            onClick={handleSignIndemnity}
            color="primary"
            variant="contained"
          >
            Sign Indemnity Agreement
          </Button>
        </DialogActions>
      </Dialog>
      <SupportRequestModal
        open={supportModalOpen}
        onClose={() => setSupportModalOpen(false)}
        onSubmit={handleSupportRequestSubmit}
        errorMessage={submissionError}
        onGoBack={() => {
          setSupportModalOpen(false);
          setActiveStep(0);
        }}
      />

      {isGuestTrusteeRequest && (
        <>
          {/* Bottom Tabs */}
          <Box
            sx={{
              position: "fixed",
              bottom: 0,
              left: 0,
              right: 0,
              zIndex: 1000,
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "center",
              pr: 2,
              gap: 1,
              flexWrap: "wrap",
            }}
          >
            {/* Support Tab */}
            <Box
              onClick={() => setOpenSupport(true)}
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 2,
                backgroundColor: "#D3D3D3",
                color: "#333",
                px: 4,
                py: 2,
                borderRadius: "8px 8px 0 0",
                cursor: "pointer",
                minWidth: "300px",
                maxWidth: "400px",
                transition: "all 0.3s ease",
                "&:hover": {
                  backgroundColor: "#C8C8C8",
                  transform: "translateY(-2px)",
                  boxShadow: "0 -6px 16px rgba(0, 0, 0, 0.2)",
                },
                "@media (max-width: 1024px)": {
                  minWidth: "260px",
                  maxWidth: "340px",
                  px: 3,
                  py: 1.5,
                  gap: 1.5,
                },
                "@media (max-width: 768px)": {
                  minWidth: "220px",
                  maxWidth: "300px",
                  px: 2,
                  py: 1,
                  gap: 1,
                },
              }}
            >
              <Box
                sx={{
                  width: 20,
                  height: 20,
                  backgroundColor: "#333",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  borderRadius: "2px",
                  flexShrink: 0,
                  "@media (max-width: 1024px)": {
                    width: 18,
                    height: 18,
                  },
                  "@media (max-width: 768px)": {
                    width: 16,
                    height: 16,
                  },
                }}
              >
                <EmailIcon
                  sx={{
                    fontSize: 14,
                    color: "white",
                    "@media (max-width: 1024px)": {
                      fontSize: 12,
                    },
                    "@media (max-width: 768px)": {
                      fontSize: 10,
                    },
                  }}
                />
              </Box>
              <Typography
                variant="body1"
                sx={{
                  fontSize: "1rem",
                  fontWeight: 500,
                  color: "#333",
                  whiteSpace: "nowrap",
                  "@media (max-width: 1024px)": {
                    fontSize: "0.9rem",
                  },
                  "@media (max-width: 768px)": {
                    fontSize: "0.8rem",
                  },
                }}
              >
                Contact Support
              </Typography>
            </Box>

            {/* Calculator Tab */}
            <Box
              onClick={() => setOpenCalculator(true)}
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 2,
                backgroundColor: "#D3D3D3",
                color: "#333",
                px: 4,
                py: 2,
                borderRadius: "8px 8px 0 0",
                cursor: "pointer",
                minWidth: "320px",
                maxWidth: "400px",
                transition: "all 0.3s ease",
                "&:hover": {
                  backgroundColor: "#C8C8C8",
                  transform: "translateY(-2px)",
                  boxShadow: "0 -6px 16px rgba(0, 0, 0, 0.2)",
                },
                "@media (max-width: 1024px)": {
                  minWidth: "280px",
                  maxWidth: "350px",
                  px: 3,
                  py: 1.5,
                  gap: 1.5,
                },
                "@media (max-width: 768px)": {
                  minWidth: "240px",
                  maxWidth: "300px",
                  px: 2,
                  py: 1,
                  gap: 1,
                },
              }}
            >
              <Box
                sx={{
                  width: 20,
                  height: 20,
                  backgroundColor: "#333",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  borderRadius: "2px",
                  flexShrink: 0,
                  "@media (max-width: 1024px)": {
                    width: 18,
                    height: 18,
                  },
                  "@media (max-width: 768px)": {
                    width: 16,
                    height: 16,
                  },
                }}
              >
                <CalculatorIcon
                  sx={{
                    fontSize: 14,
                    color: "white",
                    "@media (max-width: 1024px)": {
                      fontSize: 12,
                    },
                    "@media (max-width: 768px)": {
                      fontSize: 10,
                    },
                  }}
                />
              </Box>
              <Typography
                variant="body1"
                sx={{
                  fontSize: "1rem",
                  fontWeight: 500,
                  color: "#333",
                  whiteSpace: "nowrap",
                  "@media (max-width: 1024px)": {
                    fontSize: "0.9rem",
                  },
                  "@media (max-width: 768px)": {
                    fontSize: "0.8rem",
                  },
                }}
              >
                Use our Bond Premium Calculator
              </Typography>
            </Box>
          </Box>

          <SupportContactDialog
            open={openSupport}
            onClose={() => setOpenSupport(false)}
            context="Guest Trustee Bond Request"
          />

          <Dialog
            open={openCalculator}
            onClose={() => setOpenCalculator(false)}
            sx={{ "& .MuiPaper-root": { borderRadius: "20px", maxWidth: "90vw" } }}
          >
            <DialogContent>
              <PremiumCalculator />
            </DialogContent>
            <DialogActions>
              <Button
                onClick={() => setOpenCalculator(false)}
                sx={{
                  borderRadius: "12px",
                  color: "#333333",
                  fontWeight: 600,
                  "&:hover": { backgroundColor: "rgba(51, 51, 51, 0.08)" },
                  textTransform: "none",
                }}
              >
                Close
              </Button>
            </DialogActions>
          </Dialog>
        </>
      )}
    </>
  );
};

export default VermostTrusteeBondRequestForm;
